import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../../auth/apiFetch.ts';
import { SurfaceShell, BackLink } from '../../../components/SurfaceShell.tsx';
import { Panel, PanelGrid, Section } from '../../../components/Panel.tsx';
import { Button, IconButton } from '../../../components/Button.tsx';
import { DataTable, type Column } from '../../../components/DataTable.tsx';
import { KeyValue, type KVItem } from '../../../components/KeyValue.tsx';
import { MetricCard, MetricStrip } from '../../../components/MetricCard.tsx';
import { StateLabel } from '../../../components/StatusBadge.tsx';
import { ReasonList } from '../../../components/ReasonList.tsx';
import { LiveTag } from '../../../components/Freshness.tsx';
import {
  RibbonStack,
  type RibbonLane,
  type RibbonSegment,
} from '../../../components/Ribbon.tsx';
import { SegmentedControl, type SegmentItem } from '../../../components/Toolbar.tsx';
import {
  NO_VALUE,
  bytes,
  duration,
  midEllipsis,
  num,
  utc,
  utcTime,
} from '../../../components/format.ts';
import { Unit } from '../../../components/Unit.tsx';
import { scanState as stateOf } from '../../../components/scanState.ts';
import { useNow } from '../../../components/useNow.ts';
import { useMeasure } from '../../../components/charts/useMeasure.ts';
import { STATUS_STATE, type Tone } from '../../../components/state.ts';
import type { Reason } from '../../../components/fleetMetrics.ts';
import {
  Blank,
  blankLine,
  ReadPanel,
  SkeletonPlot,
  SkeletonRail,
  SkeletonStrip,
  SkeletonTable,
  StoppedContext,
  useRead,
  type Read,
  type SkeletonCell,
  type SkeletonColumn,
  type StandingValue,
} from '../../../components/states/index.ts';
import { Icon, type IconName } from '../../../shell/Icon.tsx';
import { useFleetRoute } from '../../../router/useFleetRoute.ts';
import {
  useDerivativeUrl,
  useRobotScans,
  useScan,
  type Scan,
  scanDerivatives,
  scanFiles,
} from '../../../hooks/useScans.ts';
import { useRobot } from '../../../hooks/useRobot.ts';
import { useTelemetry, type TelemetryReading } from '../../../hooks/useTelemetry.ts';
import { useRobotRevisions } from '../../../hooks/useRevisions.ts';
import { useContainers } from '../../../hooks/useCatalog.ts';
import { realtimeConfig } from '../../../realtime/config.ts';
import styles from './ScanViewer.module.css';

/* ==========================================================================
   Scan session — the artefact-detail page for one capture.

   The page answers, without a click: what was recorded, by which robot on
   which build, when, against which clock, where on the site, what the pipeline
   did with it and how long that took against this robot's own history, exactly
   which bytes exist and what they hash to — and then it gives the rest of the
   viewport to the point cloud.

   Everything on screen comes from the scan record, the robot record, the
   robot's other sessions or the catalog manifest, or is arithmetic on those.
   Where a fact is NOT recorded per session — the agent build in effect at
   capture time is the big one — the surface says whose value it is showing
   instead of implying it is the session's.

   The five states this route can be in are the five in `components/states`,
   and they are drawn by that module rather than by a second vocabulary here:
   a failed read keeps the grid, the KPI band, the rail's row labels and every
   column heading, prints em dashes where the figures were, and states the
   fault once on the panel that lost its supply.
   ========================================================================== */

/* three.js + the COPC/LAZ decoder (WASM) are heavy and only needed on this
   view, so the renderers are code-split. The states a session WITHOUT a point
   cloud shows are drawn here, in the page, and not by the lidar module: a
   `failed` session then never loads a renderer it will not mount, and the four
   states this stage can be in are one vocabulary owned by one file rather than
   a signature shared across two. */
const CopcCanvas = lazy(() =>
  import('./CopcCanvas.tsx').then((m) => ({ default: m.CopcCanvas })),
);

const LivePointCloudCanvas = lazy(() =>
  import('./LivePointCloudCanvas.tsx').then((m) => ({
    default: m.LivePointCloudCanvas,
  })),
);

/* --------------------------------------------------------------------------
   The scan record carries more than `useScans.ts` currently types: the
   capture's geographic bounds and the clock the recording was stamped
   against. Both are on the wire today (pipeline/models/scans.py) and both are
   load-bearing here, so they are read through a widening of the shared type
   rather than left on the floor.
   -------------------------------------------------------------------------- */
interface ScanTimebase {
  bbox_min_lon?: number | null;
  bbox_min_lat?: number | null;
  bbox_max_lon?: number | null;
  bbox_max_lat?: number | null;
  clock_wall_utc?: string | null;
  clock_monotonic_ns?: number | null;
  clock_ntp_offset_ms?: number | null;
  clock_ntp_skew_ppm?: number | null;
  clock_disciplined?: boolean | null;
}
type FullScan = Scan & ScanTimebase;

/* --------------------------------------------------------------------------
   Vocabulary and limits. Every threshold this page judges against is printed
   on the same screen it is applied on (THE BAR §13, tell #15).
   -------------------------------------------------------------------------- */

/** The NTP offset past which a capture's wall clock stops being trustworthy
 *  for cross-robot correlation. Printed in the rail beside the reading. */
const CLOCK_OFFSET_LIMIT_MS = 50;

/** Frequency error past which the agent's clock will have drifted a visible
 *  amount across a long capture. Printed in the evidence table's Limit column. */
const CLOCK_SKEW_LIMIT_PPM = 10;

/** How far the recorded wall stamp may sit from the first sample's timestamp
 *  before the two stop describing the same instant. */
const WALL_GAP_LIMIT_MS = 1_000;

/** The band, in multiples of this robot's own median write rate, inside which
 *  a capture's throughput is unremarkable. Outside it, a sensor dropped out or
 *  a bag was truncated. */
const RATE_BAND_X = 4;

/** Multiples of this robot's own median derive at which a pipeline stage stops
 *  looking slow and starts looking stuck. Printed on the derive scale. */
const DERIVE_WARN_X = 4;
const DERIVE_BAD_X = 20;

/** The scan record's poll cadence, declared by `useScan`. */
const POLL_MS = 5_000;

/* The two build-time variables `realtime/config.ts` gates the device plane on.
   Read here as well, so the stage can tell "this deployment carries no device
   plane" (a fact about the build) apart from "the subscription is not up yet"
   (a fact about the session), which the transport module answers itself. */
const iotEndpoint = (import.meta.env.VITE_IOT_ENDPOINT as string | undefined) ?? null;
const iotAuthorizer = (import.meta.env.VITE_IOT_AUTHORIZER_NAME as string | undefined) ?? null;
const iotEnv = iotEndpoint !== null && iotAuthorizer !== null ? { iotEndpoint, iotAuthorizer } : null;

/* A display vocabulary for the sensor ids the agent reports. The raw id is
   always printed beside the class, so the mapping adds a word and never
   replaces the machine string. */
const SENSOR_CLASS: Record<string, string> = {
  livox_mid360: 'lidar',
  oak_d_pro: 'depth camera',
  imu_bno085: 'imu',
  wheel_odom: 'wheel odometry',
  rplidar_s2: 'lidar',
  realsense_d435i: 'depth camera',
};

/* --------------------------------------------------------------------------
   The sensor manifest.

   The scan record lists the ids that were recording. On its own that is a list;
   against the robot's other sessions it becomes a finding — a sensor this
   robot has recorded before and did not record here is the single most useful
   thing this page can tell an operator about a capture, and it is invisible if
   the manifest is only ever read one session at a time.
   -------------------------------------------------------------------------- */

interface SensorRow {
  id: string;
  klass: string;
  /** Recording during THIS session. */
  present: boolean;
  /** Sessions of this robot that recorded it, out of `of`. */
  seen: number;
  of: number;
}

function sensorRowsOf(scan: FullScan, siblings: readonly FullScan[]): SensorRow[] {
  const pool = siblings.length > 0 ? siblings : [scan];
  const seen = new Map<string, number>();
  pool.forEach((s) => s.sensors.forEach((x) => seen.set(x, (seen.get(x) ?? 0) + 1)));
  const ids = [...new Set([...scan.sensors, ...seen.keys()])];
  return ids.map((id) => ({
    id,
    klass: sensorClass(id),
    present: scan.sensors.includes(id),
    seen: seen.get(id) ?? 0,
    of: pool.length,
  }));
}

function sensorClass(name: string): string {
  const known = SENSOR_CLASS[name];
  if (known !== undefined) return known;
  if (/livox|ouster|velodyne|lidar|mid ?360/i.test(name)) return 'lidar';
  if (/oak|realsense|zed|cam/i.test(name)) return 'camera';
  if (/imu|bno|mpu|icm/i.test(name)) return 'imu';
  if (/odom|wheel|encoder/i.test(name)) return 'odometry';
  if (/gnss|gps|rtk/i.test(name)) return 'gnss';
  return 'sensor';
}

/* --------------------------------------------------------------------------
   Small honest derivations
   -------------------------------------------------------------------------- */

function ms(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
}

/** `3.87 GiB` split so the unit can be the dimmer half of one cell. */
function splitBytes(v: number | null | undefined): { n: string; u: string } {
  const s = bytes(v);
  if (s === NO_VALUE) return { n: NO_VALUE, u: '' };
  const i = s.lastIndexOf(' ');
  return i < 0 ? { n: s, u: '' } : { n: s.slice(0, i), u: s.slice(i + 1) };
}

/** `08-30 12:57Z` — a ledger of sessions spans days, so a bare time-of-day
 *  column sorts into nonsense. Short enough to stay a 13ch column. */
function stampShort(iso: string | null | undefined): string {
  if (!iso) return NO_VALUE;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return NO_VALUE;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}Z`;
}

/** The session name an operator says out loud — the tail of the session id. */
function sessionName(sessionId: string): string {
  const i = sessionId.indexOf('_');
  return i < 0 ? sessionId : sessionId.slice(i + 1);
}

/** `s3://bucket/a/b/c.mcap` → `a/b/c.mcap`. The bucket is on the panel head. */
function objectKey(uri: string | null): string | null {
  if (!uri) return null;
  const m = /^s3:\/\/[^/]+\/(.*)$/.exec(uri);
  return m?.[1] ?? uri;
}

function baseName(uri: string | null): string | null {
  if (!uri) return null;
  const parts = uri.split('/');
  return parts[parts.length - 1] ?? null;
}

function bucketOf(scan: FullScan): string | null {
  const uri = scanFiles(scan)[0]?.storage_uri ?? scanDerivatives(scan)[0]?.storage_uri ?? null;
  if (!uri) return null;
  return /^s3:\/\/([^/]+)/.exec(uri)?.[1] ?? null;
}

/** `ghcr.io/forest3ai/f3-fleet-agent:2.10.7` → `2.10.7`. Full ref in the title. */
function imageTag(ref: string): string {
  const i = ref.lastIndexOf(':');
  return i < 0 ? ref : ref.slice(i + 1);
}

/** Metres across a WGS84 bounding box, at the box's own latitude. */
function extentMetres(s: FullScan): { w: number; h: number } | null {
  const { bbox_min_lat: a, bbox_max_lat: b, bbox_min_lon: c, bbox_max_lon: d } = s;
  if (a == null || b == null || c == null || d == null) return null;
  const mid = ((a + b) / 2) * (Math.PI / 180);
  return { h: (b - a) * 111_320, w: (d - c) * 111_320 * Math.cos(mid) };
}

/** stop → derivative, in ms. Null unless the session actually derived one. */
function deriveMs(s: FullScan): number | null {
  if (s.processing_state !== 'copc') return null;
  const stop = ms(s.stopped_utc);
  const upd = ms(s.updated_at);
  return stop === null || upd === null || upd < stop ? null : upd - stop;
}

function median(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  const v = [...values].sort((a, b) => a - b);
  const mid = v.length >> 1;
  return v.length % 2 === 1 ? (v[mid] as number) : (((v[mid - 1] as number) + (v[mid] as number)) / 2);
}

/* ==========================================================================
   THE ROBOT'S OWN RECORD OVER THE CAPTURE WINDOW.

   A lifecycle ribbon says which pipeline stage the session was in. It does
   not say how the twenty-three minutes went, and that is the question an
   operator opens this page with. The only account of those minutes the
   platform actually keeps is the robot's telemetry series — so the lanes
   below are that series, clipped to the window the ribbon already draws,
   and nothing on them is derived from anything else.

   Two of the three are read straight off the record (`state`, `battery_pct`).
   The third is arithmetic on a recorded quantity: the capture is bytes going
   onto the robot's disk, so the FALL in `disk_free_gib` between two
   consecutive readings, over the seconds between them, is the write rate the
   agent actually achieved. It is stated as such wherever it is printed, and
   its resolution — the 0.1 GiB the field is recorded to, over the cadence —
   is printed beside it, because a 0.7 MiB/s quantum on a 2.9 MiB/s signal is
   the difference between a dip and a rounding step.

   What is NOT here, because the API does not carry it: points/s or returns/s
   per bag, per-file start timestamps, and any pose-quality figure. The file
   boundaries drawn on the write lane are placed at the session's MEAN rate
   from the manifest's cumulative bytes and say so — they are the manifest on
   the axis, not five timestamps nobody recorded.
   ========================================================================== */

interface Sample {
  t: number;
  /** GiB free on the capture volume. */
  free: number | null;
  batt: number | null;
  spd: number | null;
  sats: number | null;
  mode: string | null;
  lat: number | null;
  lon: number | null;
}

/** `disk_free_gib` is recorded to this resolution, which sets the write
 *  lane's quantum once the cadence divides it. */
const DISK_STEP_GIB = 0.1;

/** A gap wider than this many cadences is an outage in the series, not a
 *  sample interval — it is drawn hollow rather than interpolated across. */
const GAP_X = 2.5;

/** Battery limits this console already publishes on the fleet surfaces. */
const BATT_LOW_PCT = 20;

function numOr(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function samplesOf(rows: readonly TelemetryReading[]): Sample[] {
  return rows
    .map((r) => ({
      t: ms(r.ts) ?? Number.NaN,
      free: numOr(r.disk_free_gib),
      batt: numOr(r.battery_pct),
      spd: numOr(r.speed_mps),
      sats: numOr((r.payload as Record<string, unknown> | null)?.['gnss_sats']),
      mode: typeof r.state === 'string' ? r.state : null,
      lat: numOr(r.lat),
      lon: numOr(r.lon),
    }))
    .filter((s) => Number.isFinite(s.t))
    .sort((a, b) => a.t - b.t);
}

/** The window, plus one sample either side so a trace reaches both edges
 *  instead of stopping at the first reading inside it. */
function windowOf(all: readonly Sample[], from: number, to: number): Sample[] {
  const inside: Sample[] = [];
  let before: Sample | null = null;
  let after: Sample | null = null;
  for (const s of all) {
    if (s.t < from) before = s;
    else if (s.t > to) {
      if (after === null) after = s;
    } else inside.push(s);
  }
  return [...(before ? [before] : []), ...inside, ...(after ? [after] : [])];
}

/** The observer's own cadence, taken as the median gap it actually delivered
 *  — never inferred from a single interval. */
function cadenceOf(s: readonly Sample[]): number | null {
  const gaps: number[] = [];
  for (let i = 1; i < s.length; i++) gaps.push((s[i] as Sample).t - (s[i - 1] as Sample).t);
  return median(gaps);
}

/** One measured interval of the write lane. A step, not a point: the rate is
 *  an average over the interval it was measured across and is drawn across
 *  exactly that interval. */
interface Step {
  t0: number;
  t1: number;
  v: number;
}

function writeSteps(s: readonly Sample[], cadence: number | null): Step[] {
  const out: Step[] = [];
  const gap = cadence === null ? Number.POSITIVE_INFINITY : cadence * GAP_X;
  for (let i = 1; i < s.length; i++) {
    const a = s[i - 1] as Sample;
    const b = s[i] as Sample;
    const dt = (b.t - a.t) / 1000;
    if (dt <= 0 || b.t - a.t > gap) continue;
    if (a.free === null || b.free === null) continue;
    const mib = (a.free - b.free) * 1024;
    /* The volume gained space: something was deleted, and a negative write
       rate is not a reading about this capture. */
    if (mib < 0) continue;
    out.push({ t0: a.t, t1: b.t, v: mib / dt });
  }
  return out;
}

interface Point {
  t: number;
  v: number;
}

function pointsOf(s: readonly Sample[], pick: (x: Sample) => number | null): Point[] {
  const out: Point[] = [];
  for (const x of s) {
    const v = pick(x);
    if (v !== null) out.push({ t: x.t, v });
  }
  return out;
}

/** The value of a step series at an instant — the interval the cursor is in,
 *  never an interpolation between two averages. */
function stepAt(steps: readonly Step[], t: number): number | null {
  for (const s of steps) if (t >= s.t0 && t <= s.t1) return s.v;
  return null;
}

/** The value of a point series at an instant, interpolated between the two
 *  readings that bracket it and refused where they are too far apart to
 *  bracket anything. */
function pointAt(pts: readonly Point[], t: number, gap: number): number | null {
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1] as Point;
    const b = pts[i] as Point;
    if (t >= a.t && t <= b.t) {
      if (b.t - a.t > gap) return null;
      const f = b.t === a.t ? 0 : (t - a.t) / (b.t - a.t);
      return a.v + (b.v - a.v) * f;
    }
  }
  return null;
}

/* --------------------------------------------------------------------------
   The cursor, shared between the lifecycle's crosshair and the ground plan.

   The plan is in the rail and the crosshair is in a panel two rows below it,
   so binding them through page state would re-render the point cloud on every
   pointer move. This is the smallest thing that does not: one value, one
   subscription, and only the plan wakes up.
   -------------------------------------------------------------------------- */

interface CursorStore {
  subscribe: (f: () => void) => () => void;
  get: () => number | null;
  set: (t: number | null) => void;
}

function makeCursor(): CursorStore {
  let at: number | null = null;
  const subs = new Set<() => void>();
  return {
    subscribe: (f) => {
      subs.add(f);
      return () => {
        subs.delete(f);
      };
    },
    get: () => at,
    set: (t) => {
      if (t === at) return;
      at = t;
      for (const f of subs) f();
    },
  };
}

/** Publishes the RibbonStack's own cursor to the store, in an effect, so no
 *  render writes to anything outside itself. */
function CursorBridge({ at, store }: { at: number | null; store: CursorStore }) {
  useEffect(() => {
    store.set(at);
  }, [at, store]);
  useEffect(() => () => store.set(null), [store]);
  return null;
}

/* --------------------------------------------------------------------------
   Derive pace — how long this session's derive took (or has been taking)
   against this robot's OWN median, and the two-level verdict that reading is
   judged by.

   One definition, three consumers: the DERIVE SCALE band draws it, the
   evidence table prints it with its limit, and the KPI band colours the
   `DERIVE` / `IN STATE` figure with it. They used to compute it three times
   and they disagreed — the scale said "this session did not derive" on a
   failed capture while the table judged the same capture 365× over the limit.
   -------------------------------------------------------------------------- */

interface DerivePace {
  /** This robot's median COMPLETED derive, and how many it was taken over. */
  median: number | null;
  n: number;
  /** Every completed derive, for the scale's tick marks. */
  samples: readonly number[];
  /** Elapsed for THIS session: stop → derivative where it finished, time in
   *  state where it is still running, null where neither applies. */
  elapsed: number | null;
  /** Multiples of the median, or null with no baseline / nothing to judge. */
  mult: number | null;
  /** Whether the elapsed reading is judged at all. A failed run aborted: the
   *  wall time since it aborted is not a derive duration and is not measured
   *  against one. */
  judged: boolean;
  tone: Tone;
}

function derivePace(
  scan: FullScan,
  siblings: readonly FullScan[],
  now: number,
): DerivePace {
  const samples = siblings.map(deriveMs).filter((v): v is number => v !== null && v > 0);
  const med = median(samples);
  const own = deriveMs(scan);
  const updated = ms(scan.updated_at);
  const failed = scan.processing_state === 'failed';
  /* `copc` reads its own stop → derivative; `raw`/`processing` read the wall
     clock since the record last moved; `failed` has neither — the run aborted,
     so the only honest figure is how long ago, and it is not a pace. */
  const elapsed = own ?? (updated === null ? null : Math.max(0, now - updated));
  const judged = !failed && elapsed !== null && med !== null && med > 0;
  const mult = judged && elapsed !== null && med !== null ? elapsed / med : null;
  const tone: Tone =
    mult === null
      ? 'neutral'
      : mult >= DERIVE_BAD_X
        ? 'error'
        : mult >= DERIVE_WARN_X
          ? 'warn'
          : 'nominal';
  return { median: med, n: samples.length, samples, elapsed, mult, judged, tone };
}

/* --------------------------------------------------------------------------
   Verification — the four groups this record can be checked in.

   Auterion's flight ledger carries a verdict column beside the software
   version; ours is derived rather than asserted, and every threshold it uses
   is printed on the same screen.

   There is ONE tally on this page and it has one denominator. The rail, the
   KPI band and the sibling ledger all show the four GROUPS; the evidence table
   below the banner shows the rows inside them. A summary and its expansion
   that disagree about how many checks there are is a console arguing with
   itself, so both are folded out of `auditRows` and neither counts anything
   the other does not.
   -------------------------------------------------------------------------- */

/* Derive first, deliberately. On the routes where this table is on screen at
   full height there is no point cloud, and "what did the pipeline do" is the
   question that put the operator here; the bytes, the clock and the capture are
   what they check next. The rail and the KPI band read in the same order. */
const AUDIT_GROUPS = ['Derive', 'Manifest', 'Clock', 'Capture'] as const;

/** What the robot register contributes to a check. */
type Register = { model?: string | null; morphology?: string | null } | undefined;

interface Check {
  key: string;
  label: string;
  /** Full sentence-ish value for the rail. */
  value: string;
  /** Compact value for a table row. */
  short: string;
  tone: Tone;
  ok: boolean;
  hollow: boolean;
  hint: string;
}

function checksFor(
  scan: FullScan,
  siblings: readonly FullScan[],
  robot: Register,
): Check[] {
  return groupsOf(auditRows(scan, siblings, robot, Date.now()));
}

/** The four group summaries, folded out of the rows the evidence table draws.
 *  Taking the rows as an argument rather than re-deriving them is what lets
 *  the KPI band, the rail and the table be one computation on one surface. */
function groupsOf(rows: readonly AuditRow[]): Check[] {
  return AUDIT_GROUPS.map((group) => {
    const mine = rows.filter((r) => r.group === group);
    const judged = mine.filter((r) => r.verdict !== 'record');
    const passed = judged.filter((r) => r.verdict === 'pass').length;
    const bad =
      mine.find((r) => r.verdict === 'fail') ?? mine.find((r) => r.verdict === 'open');
    const tone: Tone =
      bad === undefined ? 'nominal' : bad.verdict === 'fail' ? 'error' : 'warn';
    const tally = `${passed}/${judged.length}`;
    return {
      key: group.toLowerCase(),
      label: group.toUpperCase(),
      /* The rail row is 248px at a laptop's rail width, and the row's own label
         already names the group — so the value is the READING of the first row
         that is not clean, and the check that produced it is in the title with
         the rest of the group. A value that collides with its label is not a
         denser rail, it is an unreadable one. */
      value: bad === undefined ? tally : bad.reading,
      /* The ledger's cell is 22ch. A group that is failing says WHAT failed in
         the fewest characters that still identify it; the whole reading is on
         the row's own page, one click away. */
      short: bad === undefined ? tally : (bad.reading.split(' · ')[0] ?? bad.reading),
      tone,
      ok: bad === undefined,
      hollow: judged.length === 0,
      hint: [
        bad === undefined ? `${group}: ${tally} checks met` : `${group}: ${bad.check} — ${bad.reading}`,
        ...mine.map((r) => `${r.check}: ${r.reading} (limit ${r.limit})`),
      ].join('\n'),
    };
  });
}

/** Only the groups a human has to act on — plus a `4/4` when none do. */
function checkReasons(
  scan: FullScan,
  siblings: readonly FullScan[],
  robot: Register,
): Reason[] {
  const cs = checksFor(scan, siblings, robot);
  const bad = cs.filter((c) => !c.ok);
  if (bad.length === 0) {
    return [{ label: 'CHECKS', text: `${cs.length}/${cs.length}`, tone: 'nominal', hollow: false }];
  }
  return bad.map((c) => ({ label: c.label, text: c.short, tone: c.tone, hollow: c.hollow }));
}

/** An em dash in --text-low. A figure the record does not carry is never
 *  painted in a status colour and never rendered as zero. */
function Missing() {
  return <span className={styles.missing}>{NO_VALUE}</span>;
}

/* --------------------------------------------------------------------------
   A KPI figure that is also the way into the check that judges it.

   Round 3's band was eight dead ends: `CHECKS 3/4` on a stuck session and
   `CHECKS 4/4` on a clean one rendered the same colour, and neither could be
   opened. Every cell now names an evidence row — its reading, its limit and
   its verdict are in the tooltip, the row itself is one click away, and the
   colour comes from that row rather than from a second opinion.

   The wrapper is a bare flex box with no padding, border or background of its
   own, so the band's geometry, pitch and hairlines stay the strip's: nothing
   moves when a cell gains a verdict.
   -------------------------------------------------------------------------- */
function KpiCell({
  to,
  onJump,
  title,
  tone,
  children,
}: {
  /** An evidence row id, or null for the table as a whole. */
  to: string | null;
  onJump: (key: string | null) => void;
  title: string;
  /** The verdict this cell's figure carries, if it has crossed its limit. */
  tone?: Tone | undefined;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={styles.kpiJump}
      data-tone={tone}
      data-toned={tone !== undefined ? 'true' : undefined}
      aria-controls="scan-stage-surface"
      title={title}
      onClick={() => onJump(to)}
    >
      {children}
    </button>
  );
}

/* ==========================================================================
   The KPI band and the two ledgers, declared once.

   The same specs drive the live instrument and the instrument the states
   module draws in its place, so a column edge, a label and a row pitch are
   identical whether the read landed, failed or came back empty.
   ========================================================================== */

const KPI_CELLS: readonly SkeletonCell[] = [
  { label: 'Duration', sub: 'start → stop' },
  { label: 'Captured', sub: 'files' },
  { label: 'Write rate', sub: 'mean over capture' },
  { label: 'Sensors', sub: 'recording' },
  { label: 'Footprint', sub: 'bbox' },
  { label: 'Checks', sub: 'derive · manifest · clock · capture' },
  { label: 'Derived', sub: 'copc' },
  { label: 'Derive', sub: 'stop → derivative' },
];

/* --------------------------------------------------------------------------
   THE STAND-IN SHEDS THE COLUMNS THE TABLE SHEDS.

   `DataTable` drops secondary columns as its panel narrows (`drop`, widest
   first) so a 350px ledger shows three readable columns rather than seven
   crushed ones. `SkeletonTable` takes fixed widths and cannot shrink them, so
   the same seven headings in the same 350px overlapped each other — `Artefact`
   printed through `Kind` — which is the instrument mis-stating the shape of
   the table it stands in for.

   So the skeleton spec carries the SAME shed order as the real column, and
   `fitColumns` applies it against the measured panel: shed the highest `drop`
   until what is left fits at its own width plus a floor for the flexible
   column. Nothing is invented — a shed column is one the operator will not
   see when the rows land either.
   -------------------------------------------------------------------------- */
interface FitColumn extends SkeletonColumn {
  /** Shed order, mirroring the real column's. Highest goes first. */
  drop?: number | undefined;
}

/** 12px mono in this ladder measures 7.08px to the `ch`; the panels agree. */
const CH_PX = 7.08;

/* THE FLOOR IS CALIBRATED, NOT GUESSED, AND IT IS PER TABLE.

   `DataTable` sheds on MEASURED ink: it prices every column at the width its
   own content actually took, so a `Limit` cell reading `≤ 4× median (2m 00s,
   n=3)` costs 27 characters even though the column is declared at 20. A
   skeleton has no content to measure, so it prices the fixed columns at their
   declared widths and carries the whole difference in one lump — the floor
   under the flexible column.

   One global lump cannot be right for three tables with different amounts of
   un-modelled ink, and a wrong one is not a rounding error: it is a heading
   that is on screen while the panel is empty and gone the moment the rows
   land, which is THE BAR §7's "never animate layout on data arrival" broken
   by the loading state. So each spec carries its own, and each was solved
   against the real table's shed points measured at 1280 / 1600 / 1920:

     artefacts  keeps Kind only past a 565px panel   → 20ch (valid 16–22)
     sessions   sheds Duration under 963, Build under 762 → 20ch (valid 19–28)
     evidence   sheds Source under 1213px            → 30ch (valid 29–54)

   Each figure sits inside its window rather than on the edge of it, so a
   one-character change in a fixture cannot flip a column. */
const FLEX_FLOOR_CH = 12;

function fitColumns(
  spec: readonly FitColumn[],
  width: number,
  flexCh = FLEX_FLOOR_CH,
): SkeletonColumn[] {
  const kept = spec.filter(() => true);
  const fixed = (c: FitColumn): number =>
    c.width === undefined ? 0 : (parseFloat(c.width) || 0) * CH_PX;
  const total = (cs: readonly FitColumn[]): number =>
    cs.reduce((a, c) => a + fixed(c), 0) + flexCh * CH_PX;

  while (width > 0 && total(kept) > width) {
    let worst = -1;
    let at = -1;
    kept.forEach((c, i) => {
      if (c.drop !== undefined && c.drop > worst) {
        worst = c.drop;
        at = i;
      }
    });
    if (at < 0) break;
    kept.splice(at, 1);
  }
  return kept.map((c) => {
    const out: SkeletonColumn = { ...(c.label !== undefined ? { label: c.label } : {}) };
    if (c.width !== undefined) out.width = c.width;
    if (c.align !== undefined) out.align = c.align;
    return out;
  });
}

/** A skeleton table that sheds like the table it stands in for. */
function FitTable({
  spec,
  what,
  flex,
}: {
  /** Either a fixed spec or one solved from the measured width, so the
      instrument's column edges match a table whose own widths are. */
  spec: readonly FitColumn[] | ((w: number) => readonly FitColumn[]);
  what: string;
  /** This table's calibrated flexible-column floor, in `ch`. */
  flex?: number | undefined;
}) {
  const [ref, size] = useMeasure<HTMLDivElement>();
  const resolved = typeof spec === 'function' ? spec(size.w) : spec;
  return (
    <div ref={ref} className={styles.fit}>
      <SkeletonTable columns={fitColumns(resolved, size.w, flex)} what={what} />
    </div>
  );
}

/** The three calibrated floors, named where the specs are. */
const FLEX_ARTEFACT_CH = 20;
const FLEX_SESSION_CH = 20;
const FLEX_AUDIT_CH = 30;

/* --------------------------------------------------------------------------
   THE HASH IS SIZED TO THE PANEL, NOT TO THE HASH.

   A SHA-256 is 64 characters and no panel on this page is ever wide enough for
   one, so the cell has always been a middle-ellipsis. What was wrong was that
   the ellipsis was a CONSTANT: 19 characters cost 155px, the row's other three
   columns cost 240, and the artefact ledger is 350px at 1280 — so the table
   laid itself out 455px wide inside its panel, hard-clipped the hash and put
   the per-row copy button 105px outside the panel where it could be neither
   seen nor pressed. The copy button is the whole point of printing a hash you
   cannot read in full, so it is the thing that must survive; the ellipsis
   gives up characters until it does. The full 64 are on the cell's title and
   on the clipboard, and the panel's own header copies the whole manifest.
   -------------------------------------------------------------------------- */
interface ArtefactFit {
  /** Characters of the artefact name before a middle ellipsis. */
  name: number;
  /** Characters of the hash before a middle ellipsis. */
  sha: number;
}

function artefactFit(w: number): ArtefactFit {
  if (w <= 0 || w >= 430) return { name: 40, sha: 19 };
  if (w >= 395) return { name: 22, sha: 17 };
  if (w >= 360) return { name: 19, sha: 14 };
  if (w >= 330) return { name: 18, sha: 12 };
  return { name: 16, sha: 10 };
}

const artefactColumns = (w: number): readonly FitColumn[] => [
  { label: 'Artefact' },
  /* Kind sheds — a bag's extension already carries it. The hash never does:
     it gives up characters instead, and its copy affordance lives inside its
     own cell rather than in a 49px column of its own. */
  { label: 'Kind', width: '14ch', drop: 1 },
  { label: 'Size', width: '11ch', align: 'right' },
  { label: 'SHA-256', width: `${artefactFit(w).sha + 4}ch` },
];

const AUDIT_COLUMNS: readonly FitColumn[] = [
  { label: 'Group', width: '12ch' },
  { label: 'Check' },
  { label: 'Reading', width: '24ch' },
  { label: 'Limit', width: '20ch', drop: 1 },
  { label: 'Observed', width: '13ch', drop: 2 },
  { label: 'Source', width: '26ch', drop: 3 },
  { label: 'Verdict', width: '11ch' },
];

const SESSION_COLUMNS: readonly FitColumn[] = [
  { label: 'Session' },
  { label: 'Started', width: '13ch', drop: 1 },
  { label: 'State', width: '13ch' },
  { label: 'Frame', width: '8ch', drop: 3 },
  { label: 'Build', width: '15ch', drop: 2 },
  /* First to go and last to come back, exactly as in `SessionTable` — and it
     has to be HERE rather than absent, or a 1920 monitor draws seven headings
     while the ledger is empty and eight the instant it fills. */
  { label: 'Duration', width: '10ch', align: 'right', drop: 4 },
  { label: 'Size', width: '10ch', align: 'right' },
  { label: 'Checks', width: '20ch' },
];

/* The rail's chrome, in the order `RailBody` draws it — IDENTITY, then
   VERIFICATION, GROUND, BUILD, CLOCK. It has to be that order and not a
   convenient one: these labels are the SAME rows the populated rail draws, so
   a list that runs Clock before Build makes eight labels jump the moment the
   record lands, which is THE BAR §7's "never animate layout on data arrival"
   broken by the loading state rather than by the data.

   What is NOT here is anything whose row count only the record knows: the
   sensor manifest, and the container rows under BUILD. An unnamed row is a
   guess at how many there might be (`components/states` rule 4). */
const RAIL_LABELS: readonly string[] = [
  'Scan', 'Robot', 'Platform', 'Morphology', 'Format', 'Pose frame',
  'Derive', 'Manifest', 'Clock', 'Capture',
  'Extent', 'Area', 'Centre',
  'Firmware', 'Revision', 'Board', 'Bringup',
  'Wall at start', 'NTP offset', 'Skew', 'Monotonic', 'Discipline',
];

/* ==========================================================================
   The artefact ledger — capture files and their derivatives in one table.
   ========================================================================== */

interface Artefact {
  id: string;
  name: string;
  kind: string;
  role: 'capture' | 'derived';
  size: number;
  sha: string | null;
  key: string | null;
}

function artefactsOf(s: FullScan): Artefact[] {
  const files: Artefact[] = scanFiles(s).map((f, i) => ({
    id: `f${i}-${f.name}`,
    name: f.name,
    kind: s.format ?? 'file',
    role: 'capture',
    size: f.size_bytes,
    sha: f.content_sha256 || null,
    key: objectKey(f.storage_uri),
  }));
  const derived: Artefact[] = scanDerivatives(s).map((d, i) => ({
    id: `d${i}-${d.kind}`,
    name: baseName(d.storage_uri) ?? d.kind,
    kind: d.kind,
    role: 'derived',
    size: d.size_bytes,
    sha: null,
    key: objectKey(d.storage_uri),
  }));
  return [...files, ...derived];
}

/* ==========================================================================
   COPY IS A VERB AND IT HAS TO ANSWER.

   Four things on this page put something on the clipboard: an artefact's
   SHA-256, the whole sha256sum manifest, the evidence table as text, and the
   session's object prefix. `navigator.clipboard.writeText` returns a promise
   that REJECTS on an insecure origin or a denied permission, and three of the
   four used to fire it with `void` — so a refusal was silent and a success
   looked exactly the same as a dead button. An operator copying a content
   hash into an incident ticket has to know it landed.

   So one settle for all four: the glyph in the control that was pressed
   becomes a tick on success and a warning on refusal, holds for
   `COPY_HOLD_MS`, then goes back. The LABEL never changes — `Copy prefix`
   sits beside this page's one primary verb and a label that shrinks to
   `Copied` moves `Download COPC` 40px sideways for two seconds, which is THE
   BAR §7's "never animate layout" and §11.7's "a control that moves is a
   control you cannot hit without looking". The tooltip and the accessible
   name carry the words; the glyph carries the change.
   ========================================================================== */

/** How long a settled copy holds its glyph before reverting. */
const COPY_HOLD_MS = 1_400;

type CopyState = 'idle' | 'done' | 'fail';

interface Copier {
  /** How the last write settled, and which control asked for it. */
  state: CopyState;
  key: string | null;
  copy: (value: string, key?: string) => void;
}

/** The glyph a control wears for a given settlement. */
function copyIcon(s: CopyState): IconName {
  return s === 'done' ? 'check-circle' : s === 'fail' ? 'alert-triangle' : 'copy';
}

/* THE SETTLED CONTROL CHANGES COLOUR, NOT SIZE, AND NOT HUE-BY-CONTROL.
   `Button` owns its own class list, so the one step available from outside it
   is `color` — which is the step the hash cell already takes through
   `.hashCopy[data-done]`. Green for "complete" and amber for "the browser
   refused", per THE BAR §3; the accent is reserved for the current selection
   and this page's one primary verb, so a copy that worked must not borrow
   it. */
function copyInk(s: CopyState): { color: string } | undefined {
  if (s === 'done') return { color: 'var(--good-fg)' };
  if (s === 'fail') return { color: 'var(--warn-fg)' };
  return undefined;
}

/** What the control says it did, appended to its own verb. */
function copyNote(s: CopyState): string {
  return s === 'done'
    ? ' — copied'
    : s === 'fail'
      ? ' — the browser refused clipboard access'
      : '';
}

function useCopier(): Copier {
  const [mark, setMark] = useState<{ key: string | null; state: CopyState }>({
    key: null,
    state: 'idle',
  });
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    [],
  );
  const copy = useCallback((value: string, key = '') => {
    const settle = (state: CopyState) => {
      setMark({ key, state });
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = setTimeout(() => setMark({ key: null, state: 'idle' }), COPY_HOLD_MS);
    };
    /* No clipboard at all (an old engine, a hardened context) settles the
       same way a refusal does — the operator is told the value is not on the
       clipboard either way, which is the only fact they act on. */
    const write = navigator.clipboard?.writeText(value);
    if (write === undefined) {
      settle('fail');
      return;
    }
    write.then(
      () => settle('done'),
      () => settle('fail'),
    );
  }, []);
  return { state: mark.state, key: mark.key, copy };
}

/** The settlement a control keyed `key` should render, `idle` for the rest. */
function copyStateOf(c: Copier, key: string): CopyState {
  return c.key === key ? c.state : 'idle';
}

function ArtefactTable({ scan }: { scan: FullScan }) {
  const copier = useCopier();
  const [wrapRef, wrapSize] = useMeasure<HTMLDivElement>();
  const fit = artefactFit(wrapSize.w);
  const rows = useMemo(() => artefactsOf(scan), [scan]);

  const columns: readonly Column<Artefact>[] = [
    {
      key: 'name',
      header: 'Artefact',
      kind: 'id',
      mono: true,
      flex: true,
      cell: (r) => (
        <span className={styles.artefact} data-role={r.role} title={r.key ?? r.name}>
          {midEllipsis(r.name, fit.name)}
        </span>
      ),
    },
    { key: 'kind', header: 'Kind', kind: 'enum', mono: true, dim: true, drop: 1, cell: (r) => r.kind },
    {
      key: 'size',
      header: 'Size',
      kind: 'num',
      align: 'right',
      cell: (r) => {
        const b = splitBytes(r.size);
        return (
          <>
            {b.n}
            <Unit unit={b.u} />
          </>
        );
      },
    },
    /* THE HASH CELL IS ITS OWN COPY BUTTON.

       A separate icon column costs 49px — 14% of a 350px ledger at 1280 — and
       it was the thing that got pushed outside the panel when the table
       overran it. Merged, the glyph rides the value it copies, sits in the
       flow so the column never changes width when the pointer arrives, and
       the row keeps the whole affordance inside the panel at every width.
       THE BAR §6 allows exactly this: a row-level secondary action on a row
       that is one of many. */
    {
      key: 'sha',
      header: 'SHA-256',
      kind: 'id',
      mono: true,
      dim: true,
      max: `${fit.sha + 4}ch`,
      cell: (r) => {
        /* NO HASH, NO BUTTON.

           The pipeline writes `content_sha256` on capture bags and on nothing
           else, so a COPC derivative's cell has an em dash in it. It used to
           have an em dash AND a copy glyph, and the glyph quietly copied the
           object key — a control in the SHA-256 column that puts something
           that is not a hash on the clipboard, sitting beside the mark for
           "there is no value here". THE BAR §8 tell #8: an icon whose action
           you cannot name from the cell it is in is a defect. The key it was
           copying is on the row's own title and in `Copy prefix`, top-right. */
        const sha = r.sha;
        if (sha === null) {
          return (
            <span
              className={styles.missing}
              title="Not hashed — the pipeline records content_sha256 for capture bags only."
            >
              {NO_VALUE}
            </span>
          );
        }
        const st = copyStateOf(copier, r.id);
        return (
          <button
            type="button"
            className={styles.hashCopy}
            data-done={st === 'done'}
            data-fail={st === 'fail'}
            title={st === 'fail' ? 'The browser refused clipboard access' : sha}
            aria-label={`Copy SHA-256 of ${r.name}${copyNote(st)}`}
            onClick={() => copier.copy(sha, r.id)}
          >
            <span>{midEllipsis(sha, fit.sha)}</span>
            <Icon name={copyIcon(st)} size={12} />
          </button>
        );
      },
    },
  ];

  return (
    <div ref={wrapRef} className={styles.fit}>
      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(r) => r.id}
        empty="No files recorded against this session."
      />
    </div>
  );
}

/* ==========================================================================
   The sibling ledger — every session this robot has captured.

   `auterion-flights-log-table.png` carries the software version and the short
   commit on every flight row, beside a separate verification verdict. Ours
   carries the same two things — except that the build is honestly labelled as
   the robot's pin NOW, because the scan record stores `platform` and nothing
   else about what was running during the capture.
   ========================================================================== */

interface Build {
  firmware: string | null;
  revision: string | null;
  commit: string | null;
}

function SessionTable({
  rows,
  currentId,
  build,
  robot,
  onOpen,
}: {
  rows: readonly FullScan[];
  currentId: number;
  build: Build;
  robot: Register;
  onOpen: (id: number) => void;
}) {
  const columns: readonly Column<FullScan>[] = [
    {
      key: 'session',
      header: 'Session',
      kind: 'id',
      mono: true,
      flex: true,
      cell: (s) => (
        <span className={styles.sessionCell} title={s.session_id}>
          {sessionName(s.session_id)}
        </span>
      ),
    },
    {
      key: 'started',
      header: 'Started',
      kind: 'time',
      mono: true,
      dim: true,
      drop: 1,
      cell: (s) => <span title={utc(s.started_utc)}>{stampShort(s.started_utc)}</span>,
    },
    {
      key: 'state',
      header: 'State',
      kind: 'status',
      cell: (s) => {
        const st = stateOf(s.processing_state);
        return (
          <StateLabel tone={st.tone} label={st.label} loud={st.tone === 'error'} title={st.hint} />
        );
      },
    },
    {
      key: 'frame',
      header: 'Frame',
      kind: 'enum',
      mono: true,
      dim: true,
      drop: 3,
      cell: (s) => s.pose_frame ?? NO_VALUE,
    },
    {
      key: 'build',
      header: 'Build',
      kind: 'id',
      mono: true,
      dim: true,
      max: '15ch',
      drop: 2,
      /* Auterion's flight log prints version AND short commit on every row
         (auterion-flights-log-table.png), beside a separate verdict column.
         Ours prints firmware + hardware revision here and the bringup commit
         on the page's own provenance line, because in OUR data the commit is
         the robot's single current pin rather than a per-flight snapshot —
         four identical hashes down a 758px ledger would cost the Frame and
         Duration columns to say one thing once. Hence `build = pin now` in
         the panel's meta: the scan record stores `platform` and nothing else
         about the build that was actually running. */
      cell: () => (
        <span
          className={styles.build}
          title={
            `Firmware ${build.firmware ?? NO_VALUE} · revision ${build.revision ?? NO_VALUE}` +
            (build.commit === null ? '' : ` · bringup ${build.commit.slice(0, 12)}`) +
            ' — the robot’s pin as it stands now. The scan record does not snapshot the build that ran during the capture.'
          }
        >
          <span>{build.firmware ?? NO_VALUE}</span>
          <i>{build.revision ?? NO_VALUE}</i>
        </span>
      ),
    },
    {
      key: 'dur',
      header: 'Duration',
      kind: 'num',
      align: 'right',
      drop: 4,
      cell: (s) => {
        const a = ms(s.started_utc);
        const b = ms(s.stopped_utc);
        return a === null || b === null ? NO_VALUE : duration(b - a);
      },
    },
    {
      key: 'size',
      header: 'Size',
      kind: 'num',
      align: 'right',
      cell: (s) => {
        const b = splitBytes(s.size_bytes);
        return (
          <>
            {b.n}
            <Unit unit={b.u} />
          </>
        );
      },
    },
    {
      key: 'checks',
      header: 'Checks',
      kind: 'text',
      max: '22ch',
      cell: (s) => (
        <span className={styles.verdict}>
          <ReasonList reasons={checkReasons(s, rows, robot)} />
        </span>
      ),
    },
  ];

  return (
    <DataTable
      rows={rows}
      columns={columns}
      rowKey={(s) => String(s.id)}
      currentKey={String(currentId)}
      onRowClick={(s) => onOpen(s.id)}
      empty="No other sessions recorded for this robot."
    />
  );
}

/* ==========================================================================
   The site plan — every session bound this robot has, on one set of axes.

   The scan record carries a WGS84 bbox and nothing else geographic: no track,
   no waypoints. So the honest drawing is the footprint, in metres, against the
   robot's other footprints — which answers the one question the numbers cannot
   ("is this the same block as last night's?") without inventing a path.
   ========================================================================== */

interface PlanBox {
  id: number;
  name: string;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  current: boolean;
}

interface Plan {
  boxes: readonly PlanBox[];
  cx: number;
  cy: number;
  w: number;
  h: number;
  /* The local tangent-plane projection the boxes were laid out in, so a track
     read from a DIFFERENT source — the robot's telemetry fixes — lands in the
     same metres as the bounding boxes rather than in its own frame. */
  toX: (lon: number) => number;
  toY: (lat: number) => number;
}

function planOf(scans: readonly FullScan[], currentId: number): Plan | null {
  const sized = scans.filter(
    (s) =>
      s.bbox_min_lat != null &&
      s.bbox_max_lat != null &&
      s.bbox_min_lon != null &&
      s.bbox_max_lon != null,
  );
  if (sized.length === 0) return null;

  const lat0 =
    sized.reduce((a, s) => a + ((s.bbox_min_lat as number) + (s.bbox_max_lat as number)) / 2, 0) /
    sized.length;
  const lon0 =
    sized.reduce((a, s) => a + ((s.bbox_min_lon as number) + (s.bbox_max_lon as number)) / 2, 0) /
    sized.length;
  const mLat = 111_320;
  const mLon = 111_320 * Math.cos((lat0 * Math.PI) / 180);

  const boxes: PlanBox[] = sized.map((s) => ({
    id: s.id,
    name: sessionName(s.session_id),
    x0: ((s.bbox_min_lon as number) - lon0) * mLon,
    x1: ((s.bbox_max_lon as number) - lon0) * mLon,
    y0: ((s.bbox_min_lat as number) - lat0) * mLat,
    y1: ((s.bbox_max_lat as number) - lat0) * mLat,
    current: s.id === currentId,
  }));

  const minX = Math.min(...boxes.map((b) => b.x0));
  const maxX = Math.max(...boxes.map((b) => b.x1));
  const minY = Math.min(...boxes.map((b) => b.y0));
  const maxY = Math.max(...boxes.map((b) => b.y1));

  return {
    boxes,
    cx: (minX + maxX) / 2,
    cy: (minY + maxY) / 2,
    w: Math.max(maxX - minX, 40),
    h: Math.max(maxY - minY, 40),
    toX: (lon: number) => (lon - lon0) * mLon,
    toY: (lat: number) => (lat - lat0) * mLat,
  };
}

/** The capture's own track, in the plan's metres. One point per telemetry fix
 *  inside the window; nothing is interpolated and nothing is drawn where the
 *  robot did not report a position. */
interface TrackPoint {
  t: number;
  x: number;
  y: number;
}

function trackOf(plan: Plan | null, samples: readonly Sample[]): TrackPoint[] {
  if (plan === null) return [];
  const out: TrackPoint[] = [];
  for (const s of samples) {
    if (s.lat === null || s.lon === null) continue;
    out.push({ t: s.t, x: plan.toX(s.lon), y: plan.toY(s.lat) });
  }
  return out;
}

/** Ground distance between the reported fixes, in metres. A capture where
 *  this is 3 m is a bench recording, and that is the single most useful thing
 *  the track can say about a session whose footprint is 500 m wide. */
function trackMetres(pts: readonly TrackPoint[]): number {
  let d = 0;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1] as TrackPoint;
    const b = pts[i] as TrackPoint;
    d += Math.hypot(b.x - a.x, b.y - a.y);
  }
  return d;
}

const GRID_STEPS = [5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000];

/* The plan's furniture, measured rather than guessed.

   `--fs-micro` mono advances 6.02px per character in this ladder, and the
   frame reserves a 13px strip along its bottom edge for the scale caption so
   that no box stroke, no grid line and no session label is ever drawn under
   it. Round 3 printed `200 m grid · north up` straight over three bboxes. */
const PLAN_CH = 6.02;
const PLAN_FOOT = 13;
const PLAN_LABEL_H = 12;
const PLAN_LABEL_MAX_CH = 14;

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

function hits(a: Rect, b: Rect, pad = 2): boolean {
  return (
    a.x < b.x + b.w + pad &&
    b.x < a.x + a.w + pad &&
    a.y < b.y + b.h + pad &&
    b.y < a.y + a.h + pad
  );
}

function SitePlan({
  plan,
  track,
  cursor,
}: {
  plan: Plan;
  /** The current session's telemetry fixes, ascending. Empty where the robot
   *  reported no position inside the capture window. */
  track: readonly TrackPoint[];
  /** The lifecycle crosshair. Null when the pointer is not on the ribbon. */
  cursor: CursorStore;
}) {
  const [ref, size] = useMeasure<HTMLDivElement>();
  /* Only this component wakes on a cursor move — the point cloud two panels
     over must not re-render because the pointer crossed the ruler. */
  const at = useSyncExternalStore(cursor.subscribe, cursor.get, cursor.get);
  const pad = 10;
  const w = size.w;
  /* The drawing area stops short of the caption strip. */
  const h = Math.max(0, size.h - PLAN_FOOT);
  const ready = w > 40 && h > 40;

  const scale = ready
    ? Math.min((w - 2 * pad) / plan.w, (h - 2 * pad) / plan.h)
    : 0;
  const px = (x: number) => (x - plan.cx) * scale + w / 2;
  const py = (y: number) => h / 2 - (y - plan.cy) * scale;

  /* A grid whose square is a round number of metres and whose count reads at a
     glance: the first step that puts fewer than seven lines across the widest
     axis wins, so the square is the map's own scale bar. */
  const span = Math.max(plan.w, plan.h);
  const step = GRID_STEPS.find((s) => span / s <= 6) ?? 2000;

  const lines: ReactNode[] = [];
  if (ready) {
    const halfW = w / 2 / scale;
    const halfH = h / 2 / scale;
    const first = Math.ceil((plan.cx - halfW) / step) * step;
    for (let x = first; x <= plan.cx + halfW; x += step) {
      lines.push(
        <line key={`v${x}`} className={styles.planGrid} x1={px(x)} y1={0} x2={px(x)} y2={h} />,
      );
    }
    const firstY = Math.ceil((plan.cy - halfH) / step) * step;
    for (let y = firstY; y <= plan.cy + halfH; y += step) {
      lines.push(
        <line key={`h${y}`} className={styles.planGrid} x1={0} y1={py(y)} x2={w} y2={py(y)} />,
      );
    }
  }

  const rectOf = (b: PlanBox): Rect => ({
    x: px(b.x0),
    y: py(b.y1),
    w: Math.max(1, px(b.x1) - px(b.x0)),
    h: Math.max(1, py(b.y0) - py(b.y1)),
  });

  /* Labels are screen-space so 10px stays 10px whatever the scale solved to,
     and they are PLACED rather than parked at a corner: four sessions of one
     robot overlap on the ground, so four names parked at four north-west
     corners land on each other and on the boxes. The subject is laid down
     first and always keeps its name; every other name takes the first of four
     anchors — over, under, over-right, inside — that clears both the names
     already down and the subject's own box, and is dropped if none of them
     does. A dropped name is still on the rect's tooltip and in the session
     ledger two panels below; a name printed across the current capture's
     outline is a defect in the only picture on this rail. */
  const placed: Rect[] = [];
  const subject = plan.boxes.find((b) => b.current) ?? null;
  const subjectRect = ready && subject !== null ? rectOf(subject) : null;

  const labels = ready
    ? [...plan.boxes]
        .sort((a, b) => Number(b.current) - Number(a.current))
        .flatMap((b) => {
          const lw = Math.min(b.name.length, PLAN_LABEL_MAX_CH) * PLAN_CH + 4;
          const r = rectOf(b);
          const cx = (v: number) => Math.max(0, Math.min(w - lw, v));
          const cy = (v: number) => Math.max(0, Math.min(h - PLAN_LABEL_H, v));
          const tries: Rect[] = [
            /* north-west, above · south-west, below · north-east, above ·
               east of the box · west of it · inside its own north-west. */
            { x: cx(r.x), y: cy(r.y - PLAN_LABEL_H - 1), w: lw, h: PLAN_LABEL_H },
            { x: cx(r.x), y: cy(r.y + r.h + 1), w: lw, h: PLAN_LABEL_H },
            { x: cx(r.x + r.w - lw), y: cy(r.y - PLAN_LABEL_H - 1), w: lw, h: PLAN_LABEL_H },
            { x: cx(r.x + r.w + 3), y: cy(r.y), w: lw, h: PLAN_LABEL_H },
            { x: cx(r.x - lw - 3), y: cy(r.y), w: lw, h: PLAN_LABEL_H },
            { x: cx(r.x + r.w + 3), y: cy(r.y + r.h - PLAN_LABEL_H), w: lw, h: PLAN_LABEL_H },
            { x: cx(r.x - lw - 3), y: cy(r.y + r.h - PLAN_LABEL_H), w: lw, h: PLAN_LABEL_H },
            { x: cx(r.x + 2), y: cy(r.y + 1), w: lw, h: PLAN_LABEL_H },
          ];
          const spot = tries.find(
            (t) =>
              !placed.some((p) => hits(p, t)) &&
              (b.current || subjectRect === null || !hits(subjectRect, t, 0)),
          );
          if (spot === undefined) return [];
          placed.push(spot);
          return [
            <span
              key={b.id}
              className={styles.planLabel}
              data-current={b.current}
              style={{ left: `${spot.x}px`, top: `${spot.y}px` }}
            >
              {b.name}
            </span>,
          ];
        })
    : [];

  /* THE TRACK, AND THE CROSSHAIR'S POSITION ON IT.

     A bounding box says where the capture happened. It does not say how the
     robot got there, where it dwelt, or where it was at 20:11Z — and the
     crosshair on the lifecycle ruler is asking exactly that. So the fixes are
     drawn as a polyline in the same metres as the boxes, the part of it up to
     the cursor is drawn brighter than the part after, and the interpolated
     position at the cursor's instant carries a mark. With no cursor the whole
     track is one dim line and there is no mark: the console does not invent a
     position for an instant nobody is pointing at. */
  const pts = ready ? track.map((p) => ({ t: p.t, X: px(p.x), Y: py(p.y) })) : [];
  const poly = (a: readonly { X: number; Y: number }[]): string =>
    a.map((q) => `${q.X.toFixed(1)},${q.Y.toFixed(1)}`).join(' ');
  const cut = at === null ? -1 : pts.findIndex((q) => q.t > at);
  const doneN = at === null ? 0 : cut < 0 ? pts.length : cut;
  const head = pts.slice(0, doneN);
  /* Screen extent decides how the track is drawn, not the metres — the same
     3 m of drift is a legible squiggle on a 40 m plan and a sub-pixel dot on a
     700 m one. */
  const xs = pts.map((q) => q.X);
  const ys = pts.map((q) => q.Y);
  const extentPx =
    pts.length === 0
      ? 0
      : Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys));
  const moved = extentPx >= 4;
  const metres = trackMetres(track);
  const trackTitle = `${track.length} telemetry fixes during this capture · ${
    metres < 10 ? metres.toFixed(1) : Math.round(metres)
  } m travelled · lat/lon as reported by the robot${
    moved ? '' : ' — it did not move'
  }`;
  const markPt = (() => {
    if (at === null || pts.length === 0) return null;
    if (doneN === 0) return null;
    const a = pts[doneN - 1];
    const b = pts[doneN];
    if (a === undefined) return null;
    if (b === undefined) return { X: a.X, Y: a.Y };
    const f = b.t === a.t ? 0 : (at - a.t) / (b.t - a.t);
    return { X: a.X + (b.X - a.X) * f, Y: a.Y + (b.Y - a.Y) * f };
  })();

  return (
    <div className={styles.planFrame} ref={ref}>
      {ready && (
        <svg
          className={styles.planSvg}
          width={w}
          height={h}
          viewBox={`0 0 ${w} ${h}`}
          role="img"
          aria-label={`Ground footprint of ${plan.boxes.length} session${
            plan.boxes.length === 1 ? '' : 's'
          } on this robot, ${step} m grid, north up`}
        >
          {lines}
          {plan.boxes.map((b) => {
            const r = rectOf(b);
            return (
              <rect
                key={b.id}
                className={styles.planBox}
                data-current={b.current}
                x={r.x}
                y={r.y}
                width={r.w}
                height={r.h}
              >
                <title>{`${b.name}${b.current ? ' · this session' : ''}`}</title>
              </rect>
            );
          })}
          {pts.length > 1 &&
            (moved ? (
              <polyline className={styles.planTrack} points={poly(pts)}>
                <title>{trackTitle}</title>
              </polyline>
            ) : (
              /* THE ROBOT DID NOT MOVE. Nine fixes inside four metres, drawn
                 as a polyline across a 500 m plan, is an invisible line under
                 a caption that claims a track — so it becomes one mark at the
                 fixes' own position, which is the finding. */
              <circle
                className={styles.planStill}
                cx={(pts[0] as { X: number }).X}
                cy={(pts[0] as { Y: number }).Y}
                r={2}
              >
                <title>{trackTitle}</title>
              </circle>
            ))}
          {moved && head.length > 1 && (
            <polyline className={styles.planTrackRun} points={poly(head)} />
          )}
          {markPt !== null && (
            <circle className={styles.planMark} cx={markPt.X} cy={markPt.Y} r={2.5} />
          )}
        </svg>
      )}
      {labels}
      <span className={styles.planScale}>{step} m grid · north up</span>
    </div>
  );
}

/* ==========================================================================
   The derive scale — this session's pipeline latency against this robot's own.

   A limit that lives only in the code is not a limit an operator can act on,
   and "20h 08m" means nothing without the number it should have been. So the
   axis is multiples of this robot's median completed derive, every completed
   derive is a tick, the two published limits are bands, and the one mark that
   moves is this session's own elapsed.
   ========================================================================== */

const SCALE_LO = 0.5;
const SCALE_HI = 200;
const SCALE_MARKS = [1, DERIVE_WARN_X, DERIVE_BAD_X, 100];

function scalePos(mult: number): number {
  const m = Math.min(SCALE_HI, Math.max(SCALE_LO, mult));
  return (Math.log(m) - Math.log(SCALE_LO)) / (Math.log(SCALE_HI) - Math.log(SCALE_LO));
}

function DeriveScale({
  scan,
  siblings,
  now,
}: {
  scan: FullScan;
  siblings: readonly FullScan[];
  now: number;
}) {
  const pace = derivePace(scan, siblings, now);
  const { samples, elapsed, mult, tone } = pace;
  const med = pace.median;
  if (med === null) return null;

  return (
    <div className={styles.scale}>
      <div className={styles.scaleHead}>
        <span
          className={styles.scaleTitle}
          title={
            scan.processing_state === 'copc'
              ? 'Stop → derivative, against this robot’s own completed derives.'
              : 'Time in the current pipeline state, against this robot’s own completed derives.'
          }
        >
          Derive scale
        </span>
        <span className={styles.scaleAside}>
          median {duration(med)} · n={samples.length}
        </span>
      </div>
      <div
        className={styles.scaleTrack}
        title={`Multiples of this robot's median completed derive (${duration(med)}, n=${samples.length}). Amber ground past ${DERIVE_WARN_X}×, red past ${DERIVE_BAD_X}×.`}
      >
        <span
          className={styles.scaleBand}
          data-tone="warn"
          style={{
            left: `${scalePos(DERIVE_WARN_X) * 100}%`,
            right: `${(1 - scalePos(DERIVE_BAD_X)) * 100}%`,
          }}
        />
        <span
          className={styles.scaleBand}
          data-tone="error"
          style={{ left: `${scalePos(DERIVE_BAD_X) * 100}%`, right: '0%' }}
        />
        {SCALE_MARKS.map((m) => (
          <span
            key={m}
            className={styles.scaleRule}
            data-limit={m === DERIVE_WARN_X || m === DERIVE_BAD_X}
            style={{ left: `${scalePos(m) * 100}%` }}
          />
        ))}
        {samples.map((v, i) => (
          <span
            key={i}
            className={styles.scaleTick}
            style={{ left: `${scalePos(v / med) * 100}%` }}
            title={`a completed derive · ${duration(v)}`}
          />
        ))}
        {mult !== null && (
          <span
            className={styles.scaleNow}
            data-tone={tone}
            style={{ left: `${scalePos(mult) * 100}%` }}
            title={`this session · ${duration(elapsed ?? 0)} · ${mult.toFixed(1)}× median`}
          />
        )}
      </div>
      <div className={styles.scaleAxis} aria-hidden="true">
        {SCALE_MARKS.map((m) => (
          <span
            key={m}
            className={styles.scaleAxisLabel}
            data-limit={m === DERIVE_WARN_X || m === DERIVE_BAD_X}
            style={{ left: `${scalePos(m) * 100}%` }}
          >
            {m}×
          </span>
        ))}
      </div>
      <div className={styles.scaleFoot}>
        <span>
          <span className={styles.scaleKey} data-tone={tone} />
          {mult === null ? (
            /* Short on purpose: this line shares a 227px column with the axis
               at 1280, and the long form ran 43px past the panel. The full
               sentence is the track's own tooltip. */
            <u>
              {scan.processing_state === 'failed'
                ? 'aborted · no derive'
                : 'no derive recorded'}
            </u>
          ) : (
            <>
              this session {duration(elapsed ?? 0)} · <b>{mult < 10 ? mult.toFixed(1) : Math.round(mult)}×</b>
            </>
          )}
        </span>
      </div>
    </div>
  );
}

/* ==========================================================================
   Lifecycle — the capture on a real time axis.

   Round 4 spent 905×115px of this panel on three ribbon segments and a
   ruler: a time axis with nothing plotted against it. What an operator wants
   from those minutes is not "which stage" — the KPI band and the header
   already say that — it is HOW THE CAPTURE WENT, and that is a question only
   a series can answer.

   So the ruler now carries five lanes bound to one x-scale and one crosshair
   (THE BAR §5, §12.9):

     Capture   the record's own recording span
     Pipeline  registered → the state it is in
     Mode      what the ROBOT reported it was doing, from its telemetry
     Write     MiB/s achieved, from the fall in disk_free_gib, against the
               session's own mean from the manifest, with the five bag
               boundaries ticked on it
     Battery   %, against the fleet's published low limit

   The three telemetry lanes are the robot's series clipped to this window and
   nothing else: where the retention window does not reach the capture the
   lanes keep their names, their limit rules and their axis and say so, rather
   than drawing a shape nobody read.
   ========================================================================== */

type Window = 'session' | 'now';

/** The lane-label column `RibbonStack` reserves on the left. */
const RIBBON_GUTTER = 64;
/** `--fs-mono` advances this many px per character in this ladder. */
const MONO_CH = 6.62;

/* -------------------------------------------------------------------------
   One series lane: a trace on a shared x-scale, its limit rule, and its value
   at the crosshair printed at its own right edge.

   The value rides IN the lane rather than in the crosshair's box because the
   box is anchored to the top of the stack and grows downward at a fixed row
   pitch: eight rows of it is 150px in a panel whose whole body is 96, and the
   readout would be clipped by the panel it belongs to. Labelling a trace at
   its own edge is Rerun's answer to the same problem
   (`rerun-droid-multimodal-blueprint.png`) and it costs no height at all.
   ------------------------------------------------------------------------- */

interface Lane {
  key: string;
  label: string;
  unit: string;
  tone: Tone;
  lo: number;
  hi: number;
  digits: number;
  /** Averaged over an interval — drawn as a step across exactly that span. */
  steps?: readonly Step[] | undefined;
  /** Instantaneous readings — drawn as a line between them. */
  line?: readonly Point[] | undefined;
  /** The limit this channel is judged against, drawn whether or not a trace
   *  exists, because the limit does not move when the readings stop. */
  rule?: { v: number; label: string } | undefined;
  /** Vertical marks with their own meaning — the bag boundaries. */
  marks?: readonly { f: number; label: string; title: string }[] | undefined;
  /** Print each mark's ordinal. One lane in the stack does; the rest carry
   *  the tick alone, so a five-bag manifest is numbered once. */
  ordinals?: boolean | undefined;
  /** Draw the lane's own zero as a 1px rule. */
  floor?: boolean | undefined;
  /** What the rule is, spelled out for the pointer. */
  ruleTitle?: string | undefined;
  /** The value at the crosshair, or null off the series. */
  at: (t: number) => number | null;
  /** What the lane says with no crosshair on it. */
  summary: string;
  /** Nothing was read for this window. */
  blank: boolean;
}

function SeriesLanes({
  lanes,
  from,
  to,
  cursor,
}: {
  lanes: readonly Lane[];
  from: number;
  to: number;
  cursor: number | null;
}) {
  const fx = (t: number) => ((t - from) / Math.max(1, to - from)) * 1000;
  /* WHERE THE VALUE PRINTS, AND WHY IT MOVES.

     `RibbonStack`'s crosshair box is anchored to the far side of the track
     from the cursor and covers the whole lane stack — so a value pinned to
     one edge is under the box for half the ruler. It mirrors the box: the
     same 0.55 split, the opposite side. The limit takes whichever edge the
     value is not on. */
  const f = cursor === null ? null : (cursor - from) / Math.max(1, to - from);
  return (
    <div className={styles.series} data-side={f !== null && f <= 0.55 ? 'left' : 'right'}>
      {lanes.map((l) => {
        const span = Math.max(1e-6, l.hi - l.lo);
        const fy = (v: number) => 100 - ((v - l.lo) / span) * 100;
        const d: string[] = [];
        /* A STAIRCASE, NOT A DASHED LINE. Each step is an average over the
           interval it was measured across, so it is drawn flat across exactly
           that interval — and where the next interval abuts this one the two
           are joined by the vertical they actually make. Emitting the flats
           alone renders as a dashed rule, which reads as a broken series. */
        let prevEnd: number | null = null;
        for (const st of l.steps ?? []) {
          d.push(
            `${prevEnd === st.t0 ? 'L' : 'M'}${fx(st.t0).toFixed(2)} ${fy(st.v).toFixed(2)}`,
            `L${fx(st.t1).toFixed(2)} ${fy(st.v).toFixed(2)}`,
          );
          prevEnd = st.t1;
        }
        const pts = l.line ?? [];
        if (pts.length > 1) {
          d.push(
            `M${pts
              .map((q) => `${fx(q.t).toFixed(2)} ${fy(q.v).toFixed(2)}`)
              .join('L')}`,
          );
        }
        const now = cursor === null ? null : l.at(cursor);
        const value =
          now === null
            ? l.summary
            : `${now.toFixed(l.digits)} ${l.unit}`;
        /* Colour is never the only carrier, and neither is a shape: the lane
           states its channel, its span, its unit and the limit it is judged
           against as text, so the trace is readable by a screen reader and by
           anyone who cannot tell steel from blue. */
        const spoken = l.blank
          ? `${l.label}: no reading in this window${
              l.rule === undefined ? '' : `, limit ${l.rule.label} ${l.unit}`
            }`
          : `${l.label}: ${l.summary} ${l.unit}${
              l.rule === undefined ? '' : `, limit ${l.rule.label} ${l.unit}`
            }${now === null ? '' : `, ${now.toFixed(l.digits)} ${l.unit} at the cursor`}`;
        return (
          <div key={l.key} className={styles.seriesRow}>
            <span className={styles.seriesName} title={l.ruleTitle}>
              {l.label}
            </span>
            <span
              className={styles.seriesTrack}
              role="img"
              aria-label={spoken}
              title={l.ruleTitle}
            >
              <svg
                className={styles.seriesSvg}
                viewBox="0 0 1000 100"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                {l.floor === true && (
                  <line
                    className={styles.seriesFloor}
                    x1={0}
                    x2={1000}
                    y1={fy(l.lo)}
                    y2={fy(l.lo)}
                    vectorEffect="non-scaling-stroke"
                  />
                )}
                {l.rule !== undefined && l.rule.v >= l.lo && l.rule.v <= l.hi && (
                  <line
                    className={styles.seriesRule}
                    x1={0}
                    x2={1000}
                    y1={fy(l.rule.v)}
                    y2={fy(l.rule.v)}
                    vectorEffect="non-scaling-stroke"
                  />
                )}
                {d.length > 0 && (
                  <path
                    className={styles.seriesTrace}
                    data-tone={l.tone}
                    d={d.join('')}
                    vectorEffect="non-scaling-stroke"
                  />
                )}
              </svg>
              {(l.marks ?? []).map((m) => (
                <span
                  key={m.label}
                  className={styles.seriesMark}
                  style={{ left: `${(m.f * 100).toFixed(3)}%` }}
                  title={m.title}
                >
                  {l.ordinals === true && <i>{m.label}</i>}
                </span>
              ))}
              {l.rule !== undefined && (
                <span className={styles.seriesLimit} title={l.ruleTitle}>
                  {l.rule.label}
                </span>
              )}
              <span
                className={styles.seriesVal}
                data-cursor={now !== null}
                data-blank={l.blank}
              >
                {l.blank ? NO_VALUE : value}
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

function LifecyclePanel({
  scan,
  siblings,
  telemetry,
  cursor,
}: {
  scan: FullScan;
  siblings: readonly FullScan[];
  /** The robot's own series. Empty until the read lands, or where the
   *  retention window does not reach this capture. */
  telemetry: readonly Sample[];
  cursor: CursorStore;
}) {
  const st = stateOf(scan.processing_state);
  const terminal = scan.processing_state === 'copc' || scan.processing_state === 'failed';
  const [choice, setChoice] = useState<Window | null>(null);
  const mode: Window = choice ?? (terminal ? 'session' : 'now');
  const now = useNow(true);

  const startMs = ms(scan.started_utc);
  const stopMs = ms(scan.stopped_utc);
  const regMs = ms(scan.created_at);
  const updMs = ms(scan.updated_at);

  const anchor = startMs ?? regMs ?? now;
  const last = Math.max(stopMs ?? anchor, updMs ?? anchor, anchor + 1);

  /* HOW FAR PAST THE LAST EVENT THE SESSION WINDOW RUNS IS A PRESENTATION
     CHOICE, SO IT IS SIZED TO THE WORD IT HAS TO CARRY.

     The pipeline's trailing segment is the state the session is IN, and the
     ribbon prints a segment's label inside it. A flat 6% tail gave `failed`
     50px at 1600 and the ribbon rendered `fai…` — the terminal verdict, the
     one word on the lane an operator is looking for, truncated to three
     letters. The tail is now solved from the measured lane: enough pixels for
     the word plus the segment's own inset, capped at 18% so a short session
     is never mostly tail. */
  const [ribbonRef, ribbonSize] = useMeasure<HTMLDivElement>();
  const laneW = Math.max(0, ribbonSize.w - RIBBON_GUTTER);
  /* from = anchor − 0.035·(to − anchor), so a trailing block that must own
     `share` of the DRAWN window costs this multiple of the span before it. */
  const padFor = (share: number): number =>
    (1.035 * share) / Math.max(0.3, 1 - 1.035 * share);
  const span = Math.max(1, last - anchor);

  const needPx = st.label.toLowerCase().length * MONO_CH + 16;
  const tail = Math.max(span * 0.06, span * padFor(laneW > 0 ? Math.min(0.18, needPx / laneW) : 0.06));

  /* Live mode leaves the playhead its own label's width of headroom rather
     than parking it on the right edge, where `now` was clipped to nothing. */
  const live = Math.max(now, last);
  const head = (live - anchor) * padFor(laneW > 0 ? Math.min(0.1, 32 / laneW) : 0.03);
  const to = mode === 'now' ? live + head : last + tail;
  const from = anchor - (to - anchor) * 0.035;

  const capture: RibbonSegment[] = [];
  if (startMs !== null) {
    capture.push({
      start: startMs,
      end: stopMs ?? now,
      tone: 'active',
      label: 'recording',
    });
  }

  const pipeline: RibbonSegment[] = [];
  if (regMs !== null && updMs !== null && updMs > regMs) {
    pipeline.push({ start: regMs, end: updMs, tone: 'neutral', label: 'registered' });
  }
  const lastEvent = updMs ?? regMs;
  if (lastEvent !== null) {
    pipeline.push({
      start: lastEvent,
      end: Math.max(to, lastEvent + 1),
      tone: st.tone,
      label: st.label.toLowerCase(),
    });
  }

  /* ---- the robot's own record over the same window --------------------- */

  const win = useMemo(() => windowOf(telemetry, from, to), [telemetry, from, to]);
  const cadence = cadenceOf(win);
  const gap = cadence === null ? Number.POSITIVE_INFINITY : cadence * GAP_X;
  const inside = win.filter((s) => s.t >= from && s.t <= to);
  const read = inside.length > 0;

  /* MODE — the robot's reported state, as runs. A stretch the observer did not
     cover is hatched rather than filled across: an operator must be able to
     tell "idle for four minutes" from "nobody was listening for four minutes"
     without reading a legend. */
  const modeSegs: RibbonSegment[] = [];
  if (read) {
    let runStart = (win[0] as Sample).t;
    for (let i = 1; i <= win.length; i++) {
      const prev = win[i - 1] as Sample;
      const next = win[i];
      const broke =
        next === undefined || next.mode !== prev.mode || next.t - prev.t > gap;
      if (!broke) continue;
      const end = next === undefined ? Math.min(to, prev.t + (cadence ?? 0)) : next.t;
      const desc = prev.mode === null ? null : STATUS_STATE[prev.mode as keyof typeof STATUS_STATE];
      modeSegs.push({
        start: runStart,
        end: Math.max(runStart + 1, end),
        tone: desc?.tone ?? 'neutral',
        label: prev.mode ?? 'unreported',
      });
      if (next !== undefined && next.t - prev.t > gap) {
        modeSegs.push({
          start: prev.t + (cadence ?? 0),
          end: next.t,
          tone: 'neutral',
          label: 'no telemetry',
          hollow: true,
        });
      }
      if (next !== undefined) runStart = next.t;
    }
  } else {
    modeSegs.push({
      start: from,
      end: to,
      tone: 'neutral',
      label: 'no telemetry for this window',
      hollow: true,
    });
  }

  const lanes: RibbonLane[] = [
    { key: 'capture', label: 'Capture', segments: capture },
    { key: 'pipeline', label: 'Pipeline', segments: pipeline },
    { key: 'mode', label: 'Mode', segments: modeSegs },
  ];

  /* WRITE — the rate the agent actually achieved, from the fall in free disk
     between two readings. The session mean from the manifest is the rule it is
     judged against, and the field's own 0.1 GiB resolution over the cadence is
     printed as the lane's quantum, because a step that size is not a dip. */
  const steps = useMemo(() => writeSteps(win, cadence), [win, cadence]);
  const durMs = startMs !== null && stopMs !== null ? stopMs - startMs : null;
  const meanRate =
    durMs !== null && durMs > 0 ? scan.size_bytes / (durMs / 1000) / (1024 * 1024) : null;
  const peak = steps.reduce((a, s) => Math.max(a, s.v), 0);
  const wLo = steps.length === 0 ? 0 : steps.reduce((a, s) => Math.min(a, s.v), peak);
  const wHi2 = peak;
  const wHi = Math.max(peak * 1.2, (meanRate ?? 0) * 1.4, 1);
  const quantum =
    cadence === null ? null : (DISK_STEP_GIB * 1024) / (cadence / 1000);

  /* The manifest on the axis. The record carries five bag SIZES and no bag
     TIMESTAMPS, so a boundary is placed where the session's mean rate would
     have reached that many cumulative bytes — and the tick says so rather
     than implying the writer was clocked. */
  const totalBytes = scanFiles(scan).reduce((a, f) => a + f.size_bytes, 0);
  const bagMarks =
    startMs === null || stopMs === null || scanFiles(scan).length < 2 || totalBytes <= 0
      ? []
      : scanFiles(scan).slice(0, -1).reduce<{ acc: number; out: { f: number; label: string; title: string }[] }>(
          (m, f, i) => {
            const acc = m.acc + f.size_bytes;
            const t = startMs + (acc / totalBytes) * (stopMs - startMs);
            m.out.push({
              f: (t - from) / Math.max(1, to - from),
              label: String(i),
              title: `${f.name} ends here · ${bytes(f.size_bytes)} · placed at the session mean rate from cumulative bytes — the record carries no per-file timestamps`,
            });
            return { acc, out: m.out };
          },
          { acc: 0, out: [] },
        ).out;

  const batt = useMemo(() => pointsOf(win, (x) => x.batt), [win]);
  const bLo = batt.length === 0 ? 0 : Math.min(...batt.map((b) => b.v));
  const bHi = batt.length === 0 ? 100 : Math.max(...batt.map((b) => b.v));
  const bPad = Math.max(2, (bHi - bLo) * 0.25);
  const battLo = Math.max(0, Math.min(bLo - bPad, BATT_LOW_PCT - 1));
  const battHi = Math.min(100, Math.max(bHi + bPad, BATT_LOW_PCT + 4));
  /* A capture that drains the battery is a capture doing its job. The lane
     only recolours where a reading actually crossed the published limit —
     THE BAR §13, tell #15: the threshold is in the pixel, not in the
     operator's head, and nothing else on the lane changes hue. */
  const battLow = batt.some((b) => b.v <= BATT_LOW_PCT);

  const seriesLanes: Lane[] = [
    {
      key: 'write',
      label: 'Write',
      unit: 'MiB/s',
      tone: 'ok',
      lo: 0,
      hi: wHi,
      digits: 2,
      ...(read ? { steps } : {}),
      floor: true,
      ruleTitle: `The rate the agent achieved between consecutive telemetry readings, from the fall in disk_free_gib${
        quantum === null ? '' : ` (recorded to ${DISK_STEP_GIB} GiB, so ±${quantum.toFixed(1)} MiB/s at this cadence)`
      }.${
        meanRate === null
          ? ''
          : ` The dotted rule is this session's own mean, ${meanRate.toFixed(2)} MiB/s — ${bytes(scan.size_bytes)} of manifest over ${duration(durMs ?? 0)}.`
      } The 1px ticks are the manifest's ${scanFiles(scan).length} bag boundaries, placed at that mean from cumulative bytes: the record carries no per-file timestamps.`,
      ...(meanRate === null
        ? {}
        : /* `bag mean`, not `mean`: the dotted rule is the MANIFEST's mean —
             bytes on record over the recording window — while the trace is
             what the volume actually took. Naming them the same thing hides
             the one comparison this lane exists to make. */
          { rule: { v: meanRate, label: `bag mean ${meanRate.toFixed(1)}` } }),
      marks: bagMarks,
      ordinals: true,
      at: (t) => stepAt(steps, t),
      /* The lane states its own scale, the way the battery lane does — a
         trace with no range printed anywhere is a shape, not a reading. The
         sample count and the field's quantum are on the lane's tooltip. */
      summary:
        steps.length === 0
          ? NO_VALUE
          : `${wLo.toFixed(1)}–${wHi2.toFixed(1)} MiB/s · n=${steps.length}`,
      blank: !read || steps.length === 0,
    },
    {
      key: 'batt',
      label: 'Battery',
      unit: '%',
      tone: battLow ? 'warn' : 'nominal',
      lo: battLo,
      hi: battHi,
      digits: 1,
      ...(read ? { line: batt } : {}),
      rule: { v: BATT_LOW_PCT, label: `low ${BATT_LOW_PCT}` },
      ruleTitle: `${BATT_LOW_PCT}% — the fleet's published low-battery limit. Trace is battery_pct as the robot reported it during this window.`,
      marks: bagMarks,
      at: (t) => pointAt(batt, t, gap),
      summary: `${bLo.toFixed(0)}–${bHi.toFixed(0)} %`,
      blank: !read || batt.length === 0,
    },
  ];

  /* The header's facts. `capture 23m 00s` is not among them — the KPI band two
     rows above already prints DURATION, and a panel that repeats the band is
     a panel that spent a line saying nothing new. */
  const facts: { text: string; opt?: 1 }[] = [];
  if (startMs !== null && regMs !== null) {
    facts.push({ text: `registered +${duration(Math.abs(regMs - startMs))}` });
  }
  if (scan.processing_state === 'copc' && stopMs !== null && updMs !== null) {
    /* The KPI band's DERIVE cell already prints this figure, so it is the
       first thing to go when the header narrows — unlike the telemetry
       provenance below it, which is the only statement of where three of
       these five lanes came from (THE BAR §13, tell #23). */
    facts.push({ text: `derived +${duration(updMs - stopMs)} after stop`, opt: 1 });
  }
  if (updMs !== null && !terminal) {
    facts.push({ text: `${st.label.toLowerCase()} for ${duration(now - updMs)}` });
  }
  if (updMs !== null && terminal && scan.processing_state !== 'copc') {
    facts.push({ text: `${st.label.toLowerCase()} ${duration(now - updMs)} ago` });
  }
  facts.push({ text: `wall UTC ${duration(to - from)}` });
  facts.push({
    text: read
      ? `telemetry ${inside.length}${cadence === null ? '' : ` · ${Math.round(cadence / 1000)}s`}`
      : 'no telemetry in window',
  });

  return (
    <Panel
      title={
        <span className={styles.panelTitle}>
          <Icon name="history" size={14} />
          Lifecycle
        </span>
      }
      meta={
        <>
          <span
            className={styles.lifeFacts}
            title="Axis: the agent's wall clock at capture, in UTC — the same stamp the scan record carries. Mode, Write and Battery are the robot's own telemetry over the same window; Write is the fall in disk_free_gib between consecutive readings."
          >
            {facts.map((f, i) => (
              <span key={f.text} className={styles.fact} data-first={i === 0} data-opt={f.opt}>
                {f.text}
              </span>
            ))}
          </span>
          <LiveTag live={mode === 'now'} at={to} every={POLL_MS} />
        </>
      }
      tools={
        <SegmentedControl
          label="Time window"
          value={mode}
          onChange={setChoice}
          items={[
            { value: 'session', label: 'Session' },
            { value: 'now', label: 'To now' },
          ]}
        />
      }
      pad="tight"
      scroll={false}
    >
      <div className={styles.lifeBody}>
        <div className={styles.ribbonWrap} ref={ribbonRef}>
          <RibbonStack
            lanes={lanes}
            from={from}
            to={to}
            gutter={RIBBON_GUTTER}
            {...(mode === 'now' ? { now } : {})}
          >
            {(cur) => (
              <>
                <SeriesLanes lanes={seriesLanes} from={from} to={to} cursor={cur} />
                <CursorBridge at={cur} store={cursor} />
              </>
            )}
          </RibbonStack>
        </div>
        <DeriveScale scan={scan} siblings={siblings} now={now} />
      </div>
    </Panel>
  );
}

/* ==========================================================================
   The evidence table — every claim this record can actually be checked for.

   This is what stands where the point cloud would have been. A session with a
   derivative answers "did it work" with the scene itself; a session without one
   answers it with the audit, and the audit is the denser answer: 20-odd rows,
   each a reading, the limit it is judged against, the record timestamp it was
   read at, and the exact field path it came from, so an operator can re-derive
   every verdict from the JSON without trusting this page.

   Nothing here is asserted. Every reading is a field of the scan record, the
   robot register or the robot's own other sessions, or arithmetic on those.
   Where the platform does not record something — the derive attempt history is
   the one that matters — the row says so and names where the account lives,
   rather than leaving a gap the operator has to notice.
   ========================================================================== */

/** `pass` met a printed limit · `open` still outstanding · `fail` breached ·
 *  `record` the record carries the value but the console publishes no limit
 *  for it, so it is evidence rather than a verdict. */
type Verdict = 'pass' | 'open' | 'fail' | 'record';

const VERDICT_TONE: Record<Verdict, Tone> = {
  pass: 'nominal',
  open: 'neutral',
  fail: 'error',
  record: 'neutral',
};

interface AuditRow {
  id: string;
  group: string;
  /** The group's first row prints the group name; the rest print nothing. */
  head: boolean;
  check: string;
  /** Set the check name in mono when it is a machine string (a file name). */
  checkMono?: boolean | undefined;
  reading: string;
  readingTone?: Tone | undefined;
  /** Long form for the title attribute — a full hash, a full path. */
  readingFull?: string | undefined;
  limit: string;
  /** The record timestamp the reading was read at. */
  observed: string | null | undefined;
  source: string;
  verdict: Verdict;
}

function hex64(s: string | null | undefined): boolean {
  return typeof s === 'string' && /^[0-9a-f]{64}$/.test(s);
}

/** Mean bytes per second over the capture window, or null. */
function writeRate(s: FullScan): number | null {
  const a = ms(s.started_utc);
  const b = ms(s.stopped_utc);
  if (a === null || b === null || b <= a) return null;
  return s.size_bytes / ((b - a) / 1000);
}

function signed(v: number, digits: number): string {
  return `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toFixed(digits)}`;
}

function auditRows(
  scan: FullScan,
  siblings: readonly FullScan[],
  robot: { model?: string | null; morphology?: string | null } | undefined,
  now: number,
): AuditRow[] {
  const out: Omit<AuditRow, 'head'>[] = [];
  const push = (r: Omit<AuditRow, 'head'>) => out.push(r);

  const reg = scan.created_at;
  const upd = scan.updated_at;
  const others = siblings.filter((s) => s.id !== scan.id);

  /* ---- manifest ------------------------------------------------------- */
  const hashed = scanFiles(scan).filter((f) => hex64(f.content_sha256)).length;
  const keyed = scanFiles(scan).filter((f) => f.storage_uri).length;
  const sum = scanFiles(scan).reduce((a, f) => a + f.size_bytes, 0);
  const delta = sum - scan.size_bytes;

  push({
    id: 'files',
    group: 'Manifest',
    check: 'Capture bags',
    reading: `${scanFiles(scan).length}`,
    limit: '≥ 1',
    observed: reg,
    source: 'files[]',
    verdict: scanFiles(scan).length > 0 ? 'pass' : 'fail',
  });
  scanFiles(scan).forEach((f, i) => {
    push({
      id: `file-${i}`,
      group: 'Manifest',
      check: f.name,
      checkMono: true,
      reading: f.content_sha256 ? midEllipsis(f.content_sha256, 21) : NO_VALUE,
      readingFull: f.content_sha256 || undefined,
      limit: '64 hex · sha256',
      observed: reg,
      source: `files[${i}].content_sha256`,
      verdict: hex64(f.content_sha256) ? 'pass' : 'fail',
    });
  });
  push({
    id: 'reconcile',
    group: 'Manifest',
    check: 'Size reconciles',
    reading: delta === 0 ? `Δ 0 B` : `Δ ${signed(delta, 0)} B`,
    readingTone: delta === 0 ? undefined : 'warn',
    readingFull: `Σ files ${bytes(sum)} vs record ${bytes(scan.size_bytes)}`,
    limit: 'Δ = 0',
    observed: reg,
    source: 'Σ files[].size_bytes − size_bytes',
    verdict: delta === 0 ? 'pass' : 'fail',
  });
  push({
    id: 'keys',
    group: 'Manifest',
    check: 'Object keys',
    reading: `${keyed}/${scanFiles(scan).length}`,
    limit: 'every bag',
    observed: reg,
    source: 'files[].storage_uri',
    verdict: scanFiles(scan).length > 0 && keyed === scanFiles(scan).length ? 'pass' : 'fail',
  });
  push({
    id: 'hashed',
    group: 'Manifest',
    check: 'Hashes present',
    reading: `${hashed}/${scanFiles(scan).length}`,
    limit: 'every bag',
    observed: reg,
    source: 'files[].content_sha256',
    verdict: scanFiles(scan).length > 0 && hashed === scanFiles(scan).length ? 'pass' : 'fail',
  });
  push({
    id: 'sync',
    group: 'Manifest',
    check: 'Upload',
    reading: scan.sync_state ?? NO_VALUE,
    readingTone: scan.sync_state === 'synced' ? undefined : 'warn',
    limit: 'synced',
    observed: upd,
    source: 'sync_state',
    verdict: scan.sync_state === 'synced' ? 'pass' : scan.sync_state == null ? 'record' : 'open',
  });

  /* ---- clock ---------------------------------------------------------- */
  const off = scan.clock_ntp_offset_ms;
  const skew = scan.clock_ntp_skew_ppm;
  const wall = ms(scan.clock_wall_utc);
  const start = ms(scan.started_utc);
  const wallDelta = wall !== null && start !== null ? wall - start : null;

  push({
    id: 'discipline',
    group: 'Clock',
    check: 'Discipline',
    reading: scan.clock_disciplined === true ? 'NTP' : scan.clock_disciplined === false ? 'free-running' : NO_VALUE,
    readingTone: scan.clock_disciplined === true ? undefined : 'warn',
    limit: 'disciplined',
    observed: scan.clock_wall_utc ?? scan.started_utc,
    source: 'clock_disciplined',
    verdict:
      scan.clock_disciplined == null ? 'record' : scan.clock_disciplined ? 'pass' : 'fail',
  });
  push({
    id: 'offset',
    group: 'Clock',
    check: 'NTP offset',
    reading: off == null ? NO_VALUE : `${signed(off, 2)} ms`,
    readingTone: off != null && Math.abs(off) > CLOCK_OFFSET_LIMIT_MS ? 'warn' : undefined,
    limit: `± ${CLOCK_OFFSET_LIMIT_MS} ms`,
    observed: scan.clock_wall_utc ?? scan.started_utc,
    source: 'clock_ntp_offset_ms',
    verdict: off == null ? 'record' : Math.abs(off) <= CLOCK_OFFSET_LIMIT_MS ? 'pass' : 'fail',
  });
  push({
    id: 'skew',
    group: 'Clock',
    check: 'NTP skew',
    reading: skew == null ? NO_VALUE : `${signed(skew, 3)} ppm`,
    readingTone: skew != null && Math.abs(skew) > CLOCK_SKEW_LIMIT_PPM ? 'warn' : undefined,
    limit: `± ${CLOCK_SKEW_LIMIT_PPM} ppm`,
    observed: scan.clock_wall_utc ?? scan.started_utc,
    source: 'clock_ntp_skew_ppm',
    verdict: skew == null ? 'record' : Math.abs(skew) <= CLOCK_SKEW_LIMIT_PPM ? 'pass' : 'fail',
  });
  push({
    id: 'wallgap',
    group: 'Clock',
    check: 'Wall vs first sample',
    reading: wallDelta === null ? NO_VALUE : `${signed(wallDelta, 0)} ms`,
    readingTone:
      wallDelta !== null && Math.abs(wallDelta) > WALL_GAP_LIMIT_MS ? 'warn' : undefined,
    limit: `± ${num(WALL_GAP_LIMIT_MS / 1000, 0)} s`,
    observed: scan.clock_wall_utc,
    source: 'clock_wall_utc − started_utc',
    verdict:
      wallDelta === null ? 'record' : Math.abs(wallDelta) <= WALL_GAP_LIMIT_MS ? 'pass' : 'fail',
  });
  push({
    id: 'monotonic',
    group: 'Clock',
    check: 'Monotonic at start',
    reading:
      scan.clock_monotonic_ns == null ? NO_VALUE : `${num(scan.clock_monotonic_ns / 1e9, 3)} s`,
    limit: 'present',
    observed: scan.clock_wall_utc ?? scan.started_utc,
    source: 'clock_monotonic_ns',
    verdict: scan.clock_monotonic_ns == null ? 'fail' : 'pass',
  });

  /* ---- capture -------------------------------------------------------- */
  const stop = ms(scan.stopped_utc);
  const win = start !== null && stop !== null ? stop - start : null;
  const rate = writeRate(scan);
  const rates = others.map(writeRate).filter((v): v is number => v !== null && v > 0);
  const medRate = median(rates);
  const extent = extentMetres(scan);
  const key = [...scan.sensors].sort().join('|');
  const sameSensors = others.filter((s) => [...s.sensors].sort().join('|') === key).length;

  push({
    id: 'window',
    group: 'Capture',
    check: 'Recording window',
    reading: win === null ? NO_VALUE : duration(win),
    limit: 'stop > start',
    observed: scan.stopped_utc,
    source: 'started_utc → stopped_utc',
    verdict: win === null ? 'fail' : win > 0 ? 'pass' : 'fail',
  });
  push({
    id: 'rate',
    group: 'Capture',
    check: 'Write rate',
    reading: rate === null ? NO_VALUE : `${num(rate / (1024 * 1024), 1)} MiB/s`,
    readingTone:
      rate !== null && medRate !== null && (rate < medRate / RATE_BAND_X || rate > medRate * RATE_BAND_X)
        ? 'warn'
        : undefined,
    limit:
      medRate === null
        ? 'no baseline'
        : `${num(medRate / (1024 * 1024) / RATE_BAND_X, 1)}–${num((medRate / (1024 * 1024)) * RATE_BAND_X, 1)} MiB/s`,
    readingFull:
      medRate === null
        ? undefined
        : `within ${RATE_BAND_X}× of this robot's median over ${rates.length} other session${rates.length === 1 ? '' : 's'} (${num(medRate / (1024 * 1024), 1)} MiB/s)`,
    observed: scan.stopped_utc,
    source: 'size_bytes ÷ window',
    verdict:
      rate === null || medRate === null
        ? 'record'
        : rate >= medRate / RATE_BAND_X && rate <= medRate * RATE_BAND_X
          ? 'pass'
          : 'fail',
  });
  push({
    id: 'bounds',
    group: 'Capture',
    check: 'Ground bounds',
    reading: extent === null ? NO_VALUE : `${num(extent.w, 0)} × ${num(extent.h, 0)} m`,
    limit: 'non-degenerate',
    observed: scan.stopped_utc,
    source: 'bbox_min/max_lon/lat',
    verdict: extent === null ? 'fail' : extent.w > 0 && extent.h > 0 ? 'pass' : 'fail',
  });
  push({
    id: 'frame',
    group: 'Capture',
    check: 'Pose frame',
    reading: scan.pose_frame ?? NO_VALUE,
    limit: 'recorded',
    observed: reg,
    source: 'pose_frame',
    verdict: scan.pose_frame ? 'pass' : 'fail',
  });
  const absent = sensorRowsOf(scan, siblings).filter((r) => !r.present);
  push({
    id: 'sensors',
    group: 'Capture',
    check: 'Sensor manifest',
    reading:
      absent.length === 0
        ? `${scan.sensors.length} recording`
        : `${scan.sensors.length} recording · ${absent.map((r) => r.id).join(', ')} absent`,
    readingTone: absent.length === 0 ? undefined : 'warn',
    readingFull: scan.sensors.join(' · ') || undefined,
    limit:
      others.length === 0
        ? 'no baseline'
        : `same set as ${others.length} other${others.length === 1 ? '' : 's'}`,
    observed: reg,
    source: 'sensors[]',
    verdict:
      scan.sensors.length === 0
        ? 'fail'
        : others.length === 0
          ? 'record'
          : sameSensors === others.length
            ? 'pass'
            : 'fail',
  });
  push({
    id: 'platform',
    group: 'Capture',
    check: 'Platform vs register',
    reading: scan.platform ?? NO_VALUE,
    limit: robot?.model ?? 'no register',
    observed: reg,
    source: 'platform · robots.model',
    verdict:
      scan.platform == null || robot?.model == null
        ? 'record'
        : scan.platform === robot.model
          ? 'pass'
          : 'fail',
  });

  /* ---- derive --------------------------------------------------------- */
  const st = stateOf(scan.processing_state);
  const pace = derivePace(scan, siblings, now);
  const { elapsed, mult } = pace;
  const terminal = scan.processing_state === 'copc' || scan.processing_state === 'failed';
  const copcD = scanDerivatives(scan).find((d) => d.kind === 'copc' && d.storage_uri) ?? null;

  push({
    id: 'state',
    group: 'Derive',
    check: 'Pipeline state',
    reading: st.label,
    readingTone: st.tone === 'error' ? 'error' : undefined,
    limit: 'copc',
    observed: upd,
    source: 'processing_state',
    verdict:
      scan.processing_state === 'copc'
        ? 'pass'
        : scan.processing_state === 'failed'
          ? 'fail'
          : 'open',
  });
  push({
    id: 'artefact',
    group: 'Derive',
    check: 'Derivative',
    reading:
      copcD === null
        ? 'none'
        : `${bytes(copcD.size_bytes)} · 1:${Math.round(scan.size_bytes / Math.max(1, copcD.size_bytes))}`,
    readingFull: copcD?.storage_uri ?? undefined,
    limit: 'kind=copc',
    observed: upd,
    source: 'derivatives[]',
    verdict: copcD === null ? (scan.processing_state === 'failed' ? 'fail' : 'open') : 'pass',
  });
  /* A run that aborted has no pace. The wall time since it aborted is a fact
     worth printing — it is how stale the failure is — but measuring it against
     "how long a derive normally takes" makes a 12h-old failure read as a
     365×-slow derive, and the scale beside it says the opposite. So a failed
     session records the age and leaves the judging to `Pipeline state`. */
  push({
    id: 'elapsed',
    group: 'Derive',
    check:
      scan.processing_state === 'copc'
        ? 'Stop → derivative'
        : scan.processing_state === 'failed'
          ? 'Since failure'
          : 'Time in state',
    reading:
      elapsed === null
        ? NO_VALUE
        : `${duration(elapsed)}${mult === null ? '' : ` · ${mult < 10 ? mult.toFixed(1) : Math.round(mult)}×`}`,
    readingTone:
      mult === null ? undefined : mult >= DERIVE_BAD_X ? 'error' : mult >= DERIVE_WARN_X ? 'warn' : undefined,
    limit: !pace.judged
      ? scan.processing_state === 'failed'
        ? 'run aborted — not a pace'
        : 'no baseline'
      : `≤ ${DERIVE_WARN_X}× median (${duration(pace.median ?? 0)}, n=${pace.n})`,
    observed: terminal ? upd : null,
    source:
      scan.processing_state === 'copc' ? 'stopped_utc → updated_at' : 'updated_at → now',
    verdict:
      mult === null
        ? 'record'
        : mult >= DERIVE_WARN_X
          ? 'fail'
          : terminal && scan.processing_state === 'copc'
            ? 'pass'
            : 'open',
  });
  push({
    id: 'attempts',
    group: 'Derive',
    check: 'Worker attempts',
    reading: 'not recorded',
    limit: NO_VALUE,
    observed: null,
    source: `worker log · session ${scan.session_id}`,
    verdict: 'record',
  });

  /* Emitted in the order they were derived, presented in the order they are
     read. The group name is printed once, on the group's first row — a
     spanning label without a spanning row, so the pitch never breaks. */
  const seen = new Set<string>();
  return AUDIT_GROUPS.flatMap((g) =>
    out
      .filter((r) => r.group === g)
      .map((r) => {
        const head = !seen.has(r.group);
        seen.add(r.group);
        return { ...r, head };
      }),
  );
}

function AuditTable({
  rows,
  currentKey,
}: {
  rows: readonly AuditRow[];
  currentKey?: string | null | undefined;
}) {
  const columns: readonly Column<AuditRow>[] = [
    {
      key: 'group',
      header: 'Group',
      kind: 'enum',
      width: '12ch',
      cell: (r) => (r.head ? <span className={styles.auditGroup}>{r.group}</span> : null),
    },
    {
      key: 'check',
      header: 'Check',
      kind: 'identity',
      max: '26ch',
      cell: (r) => (
        <span className={r.checkMono === true ? styles.auditMono : undefined}>{r.check}</span>
      ),
    },
    {
      key: 'reading',
      header: 'Reading',
      kind: 'id',
      mono: true,
      flex: true,
      max: '26ch',
      cell: (r) => (
        <span
          className={styles.toned}
          data-tone={r.readingTone ?? 'neutral'}
          data-plain={r.readingTone === undefined}
          title={r.readingFull ?? undefined}
        >
          {r.reading}
        </span>
      ),
    },
    {
      key: 'limit',
      header: 'Limit',
      kind: 'id',
      mono: true,
      dim: true,
      max: '24ch',
      /* Shed LAST of the three secondary columns. A reading with no printed
         limit beside it is a number the operator has to judge from memory
         (THE BAR §13, tell #15); the field path it came from is the thing that
         can wait for a wider monitor. */
      drop: 1,
      cell: (r) => r.limit,
    },
    {
      key: 'observed',
      header: 'Observed',
      kind: 'time',
      mono: true,
      dim: true,
      drop: 2,
      cell: (r) =>
        r.observed === null ? (
          <span className={styles.missing}>{NO_VALUE}</span>
        ) : (
          <span title={utc(r.observed)}>{stampShort(r.observed)}</span>
        ),
    },
    {
      key: 'source',
      header: 'Source',
      kind: 'text',
      mono: true,
      dim: true,
      drop: 3,
      cell: (r) => r.source,
    },
    {
      key: 'verdict',
      header: 'Verdict',
      kind: 'status',
      width: '11ch',
      cell: (r) => (
        <StateLabel
          tone={VERDICT_TONE[r.verdict]}
          label={r.verdict}
          loud={r.verdict === 'fail'}
          hollow={r.verdict === 'record'}
        />
      ),
    },
  ];

  return (
    <DataTable
      rows={rows}
      columns={columns}
      rowKey={(r) => r.id}
      currentKey={currentKey}
      density="dense"
      empty="Nothing on this record can be checked."
    />
  );
}

/** The table as fixed-width text, for the ticket the operator is about to
 *  write. Every column the screen shows, in the order the screen shows them. */
function auditText(scan: FullScan, rows: readonly AuditRow[]): string {
  const cells = rows.map((r) => [
    r.group.toUpperCase(),
    r.check,
    r.reading,
    r.limit,
    r.observed === null || r.observed === undefined ? NO_VALUE : utc(r.observed),
    r.source,
    r.verdict,
  ]);
  const head = ['GROUP', 'CHECK', 'READING', 'LIMIT', 'OBSERVED', 'SOURCE', 'VERDICT'];
  const all = [head, ...cells];
  const w = head.map((_, i) => Math.max(...all.map((c) => (c[i] ?? '').length)));
  const line = (c: readonly string[]) =>
    c.map((v, i) => v.padEnd(i === c.length - 1 ? 0 : (w[i] ?? 0) + 2)).join('').trimEnd();
  return [
    `scan #${scan.id} · ${scan.session_id} · ${scan.robot_id} · read ${utc(new Date().toISOString())}`,
    ...all.map(line),
  ].join('\n');
}

/** `18/22 pass · 1 fail · 3 open` — the tally the panel header prints. */
function tallyOf(rows: readonly AuditRow[]): string {
  const failed = rows.filter((r) => r.verdict === 'fail').length;
  const open = rows.filter((r) => r.verdict === 'open').length;
  const judged = rows.filter((r) => r.verdict !== 'record').length;
  return (
    `${judged - failed - open}/${judged} pass` +
    (failed > 0 ? ` · ${failed} fail` : '') +
    (open > 0 ? ` · ${open} open` : '')
  );
}

/**
 * The evidence surface as it sits inside the stage panel.
 *
 * `focus` is the group an operator arrived from — the `CHECKS` cell in the KPI
 * band, or one of the four rows in the rail's VERIFICATION list. The group's
 * first row is marked as the current selection and scrolled to, so a jump from
 * `MANIFEST · syncing` lands on the manifest rows rather than at the top of a
 * 23-row table the operator then has to search.
 */
function EvidenceBody({
  scan,
  siblings,
  robot,
  focus,
  state,
}: {
  scan: FullScan;
  siblings: readonly FullScan[];
  robot: Register;
  focus: string | null;
  /** The 88px account of why there is no point cloud, where there is none. */
  state: ReactNode;
}) {
  /* Mounted only while this surface is the one on screen, so the ticker that
     advances `Time in state` never runs behind a live 3D scene. */
  const now = useNow(true);
  const rows = useMemo(
    () => auditRows(scan, siblings, robot, now),
    [scan, siblings, robot, now],
  );
  const ref = useRef<HTMLDivElement>(null);
  /* A focus is either an evidence ROW id — a KPI figure asking for the exact
     check that judges it — or a GROUP name, from the rail's four verification
     rows. The first match wins, so both entrances land somewhere real. */
  const currentKey =
    focus === null
      ? null
      : (rows.find((r) => r.id === focus)?.id ??
        rows.find((r) => r.group.toLowerCase() === focus)?.id ??
        null);

  useEffect(() => {
    if (currentKey === null) return;
    ref.current
      ?.querySelector('[data-current="true"]')
      ?.scrollIntoView({ block: 'nearest' });
  }, [currentKey]);

  return (
    <div className={styles.evidenceWrap} ref={ref}>
      {state}
      <div className={styles.evidenceScroll}>
        <AuditTable rows={rows} currentKey={currentKey} />
      </div>
    </div>
  );
}

/* ==========================================================================
   The stage — the point cloud, and the designed states where there isn't one.
   ========================================================================== */

type Source = 'preview' | 'full' | 'live';

/** The source strip's value space. `none` is not a source an operator can
 *  pick — it is the state of the strip when no segment is one they could be
 *  on, and it exists so the control can say that rather than claim one. */
type SourcePick = Source | 'none';

/* --------------------------------------------------------------------------
   The stage's two surfaces.

   A session that derived a point cloud answers "did this capture work" with
   the scene; a session that did not answers it with the audit. Round 2 gave
   the audit only to the second kind, which put the console's own reasoning
   behind a dead end on exactly the sessions an operator is about to ship
   downstream: `CHECKS 4/4` is the moment you most want to see WHICH limit each
   reading was judged against. So the two surfaces are verb tabs on the stage's
   own header row, on every state this page can be in, and the KPI cell and the
   rail's four verification rows are the other way in.
   -------------------------------------------------------------------------- */

type StageTab = 'viewer' | 'evidence';

const STAGE_TABS: readonly { id: StageTab; label: string; icon: IconName }[] = [
  { id: 'viewer', label: 'Viewer', icon: 'cube' },
  { id: 'evidence', label: 'Evidence', icon: 'check-circle' },
];

function StageTabs({
  active,
  onChange,
  count,
  viewerReason,
}: {
  active: StageTab;
  onChange: (t: StageTab) => void;
  count: number | undefined;
  /** Why the viewer cannot be selected, printed on the tab. A disabled control
   *  with no reason is worse than an enabled one that fails (THE BAR §11.10). */
  viewerReason?: string | undefined;
}) {
  const usable = STAGE_TABS.filter(
    (t) => !(t.id === 'viewer' && viewerReason !== undefined),
  );
  const move = (e: ReactKeyboardEvent<HTMLSpanElement>) => {
    const i = usable.findIndex((t) => t.id === active);
    if (i < 0) return;
    let next = i;
    if (e.key === 'ArrowRight') next = (i + 1) % usable.length;
    else if (e.key === 'ArrowLeft') next = (i - 1 + usable.length) % usable.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = usable.length - 1;
    else return;
    e.preventDefault();
    const t = usable[next];
    if (t) onChange(t.id);
  };

  return (
    <span
      className={styles.headTabs}
      role="tablist"
      aria-label="Capture surfaces"
      onKeyDown={move}
    >
      {STAGE_TABS.map((t) => {
        const on = t.id === active;
        const off = t.id === 'viewer' && viewerReason !== undefined;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            id={`scan-stage-tab-${t.id}`}
            aria-selected={on}
            aria-controls="scan-stage-surface"
            tabIndex={on ? 0 : -1}
            disabled={off}
            className={styles.headTab}
            title={off ? viewerReason : undefined}
            onClick={() => onChange(t.id)}
          >
            <Icon name={t.icon} size={13} />
            {t.label}
            {t.id === 'evidence' && count !== undefined && <i>({count})</i>}
            {off && <u>{viewerReason}</u>}
          </button>
        );
      })}
    </span>
  );
}

/** The height a lazily-loaded renderer's chunk holds, so the row does not
 *  jump when the module lands. */
function BannerHold() {
  return <div className={styles.bannerHold} aria-hidden="true" />;
}

/* ==========================================================================
   THE STAGE'S DESIGNED STATES — two lines, no prose.

   A session with no point cloud still has to answer WHY, and round 3 answered
   it with a sentence-case headline plus two lines of 12px sans running ~820px
   across — the only multi-line prose on this screen, and every fact in it was
   already printed, better, in the mono grid beside it ("The raw session bags
   are untouched" = `Retained 3 files · 1.4 GiB`; "The API records no failure
   reason" = `Reason not reported by API`).

   So the account is two 26px rows and nothing else:

     1  [glyph] PHRASE   action · in mono                        entity/path
     2  Label value · Label value · Label value · …

   Row 1 is the state in the fewest words that are still true, then the mono
   action line — what is absent, and what makes it exist. Row 2 is the
   provenance run `auterion-vehicle-detail-overview.png` puts on ONE 20px line
   rather than in a panel: label dim, value bright, `·` between. 52px where the
   banner was 88px, and the ~36px reclaimed goes to the evidence table under it
   on three of this route's four states.
   ========================================================================== */

interface StateFact {
  label: string;
  value: string;
  tone?: 'error' | undefined;
  title?: string | undefined;
}

interface StageStateSpec {
  entity: string;
  glyph: IconName;
  tone?: Tone | undefined;
  phrase: string;
  action: string;
  facts: readonly StateFact[];
  /** A derive that is genuinely running gets the 1px bar (THE BAR §6). */
  loading?: boolean | undefined;
}

function StageState({ spec }: { spec: StageStateSpec }) {
  const { entity, glyph, tone, phrase, action, facts, loading = false } = spec;
  /* The discriminating tail bright, the prefix dim — the same reading the
     viewport bar gives an entity path. */
  const cut = entity.lastIndexOf('/') + 1;
  return (
    <div className={styles.state} data-tone={tone}>
      <div className={styles.stateLine}>
        <Icon name={glyph} size={13} />
        <span className={styles.statePhrase}>{phrase}</span>
        <span className={styles.stateAction}>{action}</span>
        <span className={styles.stateGrow} />
        <span className={styles.stateEntity} title={entity}>
          <span>{entity.slice(0, cut)}</span>
          {entity.slice(cut)}
        </span>
      </div>
      <div className={styles.stateFacts}>
        {facts.map((f) => (
          <span
            key={f.label}
            className={styles.stateFact}
            {...(f.title !== undefined ? { title: f.title } : {})}
          >
            <span className={styles.stateFactLabel}>{f.label}</span>
            <span className={styles.stateFactValue} data-tone={f.tone}>
              {f.value}
            </span>
          </span>
        ))}
      </div>
      {loading && (
        <div className={styles.stateBar} role="progressbar" aria-label={phrase} />
      )}
    </div>
  );
}

/** The ticking states. The clock is mounted HERE and not in `Stage`, so a
 *  session that did render a point cloud never re-renders the scene at 1 Hz to
 *  advance a figure it is not showing. */
function PipelineState({ scan }: { scan: FullScan }) {
  return <StageState spec={pipelineState(scan, useNow(true))} />;
}

/** `3 files · 1.4 GiB` — what object storage is holding for this session. */
function fileFact(scan: FullScan): string {
  const n = scanFiles(scan).length;
  return `${n} file${n === 1 ? '' : 's'} · ${bytes(scan.size_bytes)}`;
}

/** The two derivative-less lifecycle states, and the pace they are read at. */
function pipelineState(scan: FullScan, now: number): StageStateSpec {
  const at = ms(scan.updated_at);
  const age = at === null ? NO_VALUE : duration(now - at);
  const entity = `scans/${scan.id}/${scan.session_id}`;
  if (scan.processing_state === 'failed') {
    return {
      entity,
      glyph: 'alert-octagon',
      tone: 'error',
      phrase: 'COPC derive failed',
      action: 'derivative kind=copc absent · re-run processing to retry',
      facts: [
        { label: 'Failed', value: at === null ? NO_VALUE : `${age} ago`, tone: 'error' },
        { label: 'At', value: utc(scan.updated_at ?? null) },
        {
          label: 'Retained',
          value: fileFact(scan),
          title: 'Still in object storage — the bags the failed run read are intact, so a retry reads the same input.',
        },
        { label: 'Derivative', value: 'none' },
        { label: 'Reason', value: 'not reported by API' },
      ],
    };
  }
  const raw = scan.processing_state === 'raw';
  return {
    entity,
    glyph: 'cube',
    tone: raw ? 'neutral' : 'ok',
    phrase: raw ? 'Derive not dispatched' : 'Deriving the point cloud',
    action: raw
      ? 'no derivative requested · dispatch to queue the COPC worker'
      : 'awaiting derivative kind=copc · polled with the scan record',
    loading: !raw,
    facts: [
      { label: raw ? 'Raw for' : 'Processing for', value: age },
      { label: 'Since', value: utc(scan.updated_at ?? null) },
      {
        label: 'Captured',
        value: fileFact(scan),
        title: 'Capture bags held in object storage — the worker\u2019s input.',
      },
      { label: 'Derivative', value: raw ? 'none' : 'none yet' },
      raw
        ? { label: 'Worker', value: 'not dispatched' }
        : { label: 'Poll', value: 'every 5 s' },
    ],
  };
}

function Stage({
  scan,
  siblings,
  robot,
  tab,
  onTab,
  focusGroup,
  source,
  onSource,
  previewUrl,
  fullUrl,
  urlError,
  urlLoading,
  notice,
  liveCarried,
}: {
  scan: FullScan;
  siblings: readonly FullScan[];
  robot: Register;
  tab: StageTab;
  onTab: (t: StageTab) => void;
  focusGroup: string | null;
  source: Source;
  onSource: (s: Source) => void;
  previewUrl: string | null;
  fullUrl: string | null;
  urlError: string | null;
  urlLoading: boolean;
  notice: string | null;
  liveCarried: boolean;
}) {
  const st = stateOf(scan.processing_state);
  const copier = useCopier();
  const copc = scanDerivatives(scan).find((d) => d.kind === 'copc' && d.storage_uri);
  const preview = scanDerivatives(scan).find((d) => d.kind === 'copc-preview' && d.storage_uri);

  const onStreamEnd = useCallback(() => onSource(copc ? 'full' : 'preview'), [onSource, copc]);

  /* An explicit, always-visible decimation readout (THE BAR §12.10). The
     operator is never left guessing whether the scene is a subset. */
  let lod: string;
  if (source === 'live') lod = 'decimated live frames';
  else if (source === 'preview' && preview && copc) {
    lod = `preview · ${bytes(preview.size_bytes)} · 1:${(copc.size_bytes / preview.size_bytes).toFixed(1)} of full`;
  } else if (source === 'preview' && preview) {
    lod = `preview · ${bytes(preview.size_bytes)}`;
  } else if (copc) {
    lod = `full · ${bytes(copc.size_bytes)} · 1:${Math.round(scan.size_bytes / copc.size_bytes)} of capture`;
  } else {
    lod = 'no derivative';
  }

  /* A scene is mounted only where a renderer actually has a URL to draw. */
  const url = source === 'preview' ? previewUrl : fullUrl;
  const scene =
    (source === 'live' && liveCarried) ||
    (source !== 'live' &&
      scan.processing_state !== 'failed' &&
      (copc !== undefined || preview !== undefined) &&
      urlError === null &&
      url !== null);

  /* The three segments are drawn on every state — a derivative that does not
     exist yet is a declared capability, greyed, the way
     `auterion-vehicle-detail-overview.png` keeps the `Video` chip present and
     grey on an offline vehicle. Their WIDTHS therefore never move when a
     derivative lands, which is THE BAR §7's "never animate layout on data
     arrival" applied to the control rather than to the data. */
  const items: readonly SegmentItem<SourcePick>[] = [
    { value: 'preview', label: 'Preview', disabled: !preview },
    { value: 'full', label: 'Full', disabled: !copc },
    { value: 'live', label: 'Live' },
  ];

  const kindPath = source === 'preview' ? 'copc-preview' : 'copc';
  const derivEntity = `scans/${scan.id}/derivatives/${kindPath}`;
  const derivBytes = bytes(copc?.size_bytes ?? preview?.size_bytes ?? null);

  let body: ReactNode;
  if (source === 'live' && iotEnv === null) {
    /* No device plane in this deployment at all — there is nothing to
       subscribe to, so this is a fact about the build's environment and not a
       stream that is down. Answering it here rather than mounting the MQTT
       module keeps the four states this stage can show in ONE vocabulary, and
       spends no chunk on a transport that cannot open. Everything past this
       gate — connecting, subscribed-but-silent, ended — is the transport's own
       account and stays with it. */
    body = (
      <StageState
        spec={{
          entity: `robots/${scan.robot_id}`,
          glyph: 'antenna-off',
          tone: 'neutral',
          phrase: 'Live stream not configured',
          action: 'no device plane in this build · nothing to subscribe to',
          facts: [
            { label: 'Transport', value: 'MQTT 5 / WSS · AWS IoT Core' },
            { label: 'IoT endpoint', value: iotEndpoint ?? 'unset' },
            { label: 'Authorizer', value: iotAuthorizer ?? 'unset' },
            { label: 'Robot', value: scan.robot_id },
            { label: 'Fallback', value: 'REST polling · every 5 s' },
          ],
        }}
      />
    );
  } else if (source === 'live') {
    body = (
      <Suspense fallback={<BannerHold />}>
        <LivePointCloudCanvas robotId={scan.robot_id} onStreamEnd={onStreamEnd} />
      </Suspense>
    );
  } else if (scan.processing_state === 'failed' || (!copc && !preview)) {
    body = <PipelineState scan={scan} />;
  } else if (urlError !== null) {
    body = (
      <StageState
        spec={{
          entity: derivEntity,
          glyph: 'alert-octagon',
          tone: 'error',
          phrase: 'No signed read URL',
          action: 'derivative row present · signing failed, the object is untouched',
          facts: [
            { label: 'Endpoint', value: `GET /api/scans/${scan.id}/derivatives/…/url` },
            { label: 'Reply', value: urlError, tone: 'error' },
            { label: 'Object', value: derivBytes },
          ],
        }}
      />
    );
  } else if (url === null) {
    body = (
      <StageState
        spec={{
          entity: derivEntity,
          glyph: 'cube',
          /* Explicitly neutral: the host wrapper carries the SESSION's tone
             (`copc` is green) and a signing step inheriting it would print an
             in-flight console step as a verdict on the capture. */
          tone: 'neutral',
          phrase: 'Minting a signed read URL',
          action: 'range-read from object storage needs a short-lived signed GET',
          loading: true,
          facts: [
            { label: 'Endpoint', value: `GET /api/scans/${scan.id}/derivatives/…/url` },
            { label: 'Object', value: derivBytes },
          ],
        }}
      />
    );
  } else {
    body = (
      <Suspense fallback={<BannerHold />}>
        <CopcCanvas key={source} url={url} />
      </Suspense>
    );
  }

  /* Two surfaces, and the rule for which one an operator may be on.

     With a scene the two compete for the same frame and the tabs choose
     between them. WITHOUT one there is no second surface to choose: the
     "viewer" is an 88px account of why there is no cloud, which is a state
     header, not a picture. So the banner rides above the evidence table in one
     panel and the viewer tab stands down with its reason printed on it —
     rather than becoming a tab that hands the operator 500px of bare ground.
     The source control stays on the header either way, so `Live` is reachable
     from both, and choosing a source that CAN render brings the viewer back. */
  const evidence = tab === 'evidence' || !scene;

  /* The tally and the row count are read off the same function the table
     draws, so the header and its expansion can never disagree about how many
     checks there are. Read once per render rather than on a ticker: the table
     itself is the thing that has to advance every second, and it does. */
  const rows = auditRows(scan, siblings, robot, Date.now());

  const viewerReason = scene
    ? undefined
    : source === 'live'
      ? 'no stream'
      : scan.processing_state === 'failed'
        ? /* the same two words the banner's own meta prints, so the tab and the
             surface under it are one statement rather than two */
          'no artefact'
        : urlError !== null
          ? 'no read URL'
          : scan.processing_state === 'raw'
            ? 'not requested'
            : 'derivative pending';

  /* A DISABLED SEGMENT IS NEVER THE CURRENT ONE.

     The default source is `full`, and on a session that is still deriving,
     failed, or was never dispatched there IS no full — so the strip drew
     `Full` greyed AND accent-underlined at the same time, which says "you are
     looking at the full cloud" on a page whose own banner two rows below says
     the derivative does not exist. A radiogroup whose checked option is
     `aria-disabled` is the same lie to a screen reader.

     So the check mark goes to the source only when that source is one the
     operator could actually be on. `Live` is never disabled, so a deliberate
     switch to it stays marked even where the transport is missing and the
     stage is showing why. Where nothing can render, nothing is checked — the
     honest answer to "which point cloud am I looking at" is none. */
  const current: SourcePick =
    items.find((i) => i.value === source)?.disabled === true ? 'none' : source;

  const sourcePicker = (
    <SegmentedControl<SourcePick>
      label="Point-cloud source"
      value={current}
      onChange={(next) => {
        if (next !== 'none') onSource(next);
      }}
      items={items}
    />
  );

  return (
    <Panel
      viewport={scene && !evidence}
      pad="none"
      scroll={false}
      loading={urlLoading && !evidence}
      className={styles.stagePanel}
      title={
        <StageTabs
          active={evidence ? 'evidence' : 'viewer'}
          onChange={onTab}
          count={rows.length}
          viewerReason={viewerReason}
        />
      }
      meta={
        evidence ? (
          <>
            <span
              className={styles.metaWord}
              title="Every reading is a field of the scan record, the robot register, or this robot's own other sessions. Nothing here is asserted by the console."
            >
              {tallyOf(rows)}
            </span>
            <LiveTag live at={scan.updated_at} every={POLL_MS} />
          </>
        ) : (
          <>
            <span className={styles.metaWord} title="Resolution actually being rendered">
              {lod}
            </span>
            {/* Per-panel liveness. A mounted scene is a RECORDING of a capture
                that stopped — `Live off` at the stop. */}
            {source === 'live' ? (
              <LiveTag live={false} label="Stream" />
            ) : (
              <LiveTag live={false} at={scan.stopped_utc} label="Live" />
            )}
          </>
        )
      }
      tools={
        evidence ? (
          <>
            {!scene && sourcePicker}
            <IconButton
              icon={copyIcon(copyStateOf(copier, 'audit'))}
              style={copyInk(copyStateOf(copier, 'audit'))}
              label={`Copy the evidence table as text${copyNote(copyStateOf(copier, 'audit'))}`}
              onClick={() => copier.copy(auditText(scan, rows), 'audit')}
            />
          </>
        ) : (
          sourcePicker
        )
      }
    >
      <div
        className={styles.stageWrap}
        id="scan-stage-surface"
        role="tabpanel"
        aria-labelledby={`scan-stage-tab-${evidence ? 'evidence' : 'viewer'}`}
      >
        {/* The one place a MUTATION can fail on this page. It gets the same
            shape a read fault gets — a dense --error line flush to the panel's
            edges, above the surface it applies to — rather than a toast the
            operator can miss, or the silence round 3 shipped when the class
            it named did not exist in the sheet. */}
        {notice !== null && (
          <div className={styles.dispatchFault} data-tone="error" role="status">
            <Icon name="alert-triangle" size={13} />
            <span>
              POST /api/scans/{scan.id}/process · {notice}
            </span>
          </div>
        )}
        {evidence ? (
          <EvidenceBody
            scan={scan}
            siblings={siblings}
            robot={robot}
            focus={focusGroup}
            state={
              scene ? null : (
                <div className={styles.stageState} data-tone={st.tone}>
                  {body}
                </div>
              )
            }
          />
        ) : (
          <div className={styles.stageBody} data-viewport={scene} data-tone={st.tone}>
            {body}
          </div>
        )}
      </div>
    </Panel>
  );
}

/* ==========================================================================
   The rail — the session's full spec sheet.
   ========================================================================== */

function Rail({
  scan,
  read,
  robotName,
  build,
  board,
  containers,
  kind,
  site,
  plan,
  track,
  cursor,
  checks,
  sensors,
  sensorNote,
  focusGroup,
  onJump,
}: {
  scan: FullScan | undefined;
  read: Read;
  robotName: string | null;
  build: Build;
  board: string | null;
  containers: readonly { name: string; value: string | null }[];
  kind: string | null;
  site: string | null;
  plan: Plan | null;
  track: readonly TrackPoint[];
  cursor: CursorStore;
  checks: readonly Check[];
  sensors: readonly SensorRow[];
  sensorNote: string;
  focusGroup: string | null;
  onJump: (group: string | null) => void;
}) {
  return (
    <ReadPanel
      title="Session"
      read={read}
      pad="default"
      className={styles.rail}
      meta={<span className={styles.metaWord}>{kind ?? NO_VALUE}</span>}
      skeleton={<SkeletonRail labels={RAIL_LABELS} what="this session" />}
    >
      {scan === undefined ? null : (
        <RailBody
          scan={scan}
          robotName={robotName}
          build={build}
          board={board}
          containers={containers}
          site={site}
          plan={plan}
          track={track}
          cursor={cursor}
          checks={checks}
          sensors={sensors}
          sensorNote={sensorNote}
          focusGroup={focusGroup}
          onJump={onJump}
        />
      )}
    </ReadPanel>
  );
}

function RailBody({
  scan,
  robotName,
  build,
  board,
  containers,
  site,
  plan,
  track,
  cursor,
  checks,
  sensors,
  sensorNote,
  focusGroup,
  onJump,
}: {
  scan: FullScan;
  robotName: string | null;
  build: Build;
  board: string | null;
  containers: readonly { name: string; value: string | null }[];
  site: string | null;
  plan: Plan | null;
  track: readonly TrackPoint[];
  cursor: CursorStore;
  checks: readonly Check[];
  sensors: readonly SensorRow[];
  sensorNote: string;
  focusGroup: string | null;
  onJump: (group: string | null) => void;
}) {
  const extent = extentMetres(scan);
  const off = scan.clock_ntp_offset_ms;
  const offBad = off != null && Math.abs(off) > CLOCK_OFFSET_LIMIT_MS;

  const identity: KVItem[] = [
    { label: 'Scan', value: `#${scan.id}` },
    { label: 'Robot', value: scan.robot_id, title: robotName ?? undefined },
    { label: 'Platform', value: scan.platform ?? NO_VALUE },
    { label: 'Morphology', value: scan.morphology ?? NO_VALUE },
    { label: 'Format', value: scan.format ?? NO_VALUE },
    { label: 'Pose frame', value: scan.pose_frame ?? NO_VALUE },
  ];

  const ground: KVItem[] = extent
    ? [
        { label: 'Extent', value: `${num(extent.w, 0)} × ${num(extent.h, 0)}`, unit: 'm' },
        { label: 'Area', value: num((extent.w * extent.h) / 10_000, 2), unit: 'ha' },
        {
          label: 'Centre',
          value: `${(((scan.bbox_min_lat ?? 0) + (scan.bbox_max_lat ?? 0)) / 2).toFixed(5)}, ${(((scan.bbox_min_lon ?? 0) + (scan.bbox_max_lon ?? 0)) / 2).toFixed(5)}`,
        },
      ]
    : [{ label: 'Bounds', value: NO_VALUE }];

  const clock: KVItem[] = [
    { label: 'Wall at start', value: utc(scan.clock_wall_utc ?? scan.started_utc) },
    {
      label: 'NTP offset',
      value:
        off == null ? (
          NO_VALUE
        ) : (
          <span className={styles.toned} data-tone={offBad ? 'warn' : 'nominal'}>
            {off > 0 ? '+' : ''}
            {off.toFixed(2)}
          </span>
        ),
      unit: off == null ? undefined : 'ms',
      title: `Limit ±${CLOCK_OFFSET_LIMIT_MS} ms`,
    },
    {
      label: 'Skew',
      value: scan.clock_ntp_skew_ppm == null ? NO_VALUE : scan.clock_ntp_skew_ppm.toFixed(3),
      unit: scan.clock_ntp_skew_ppm == null ? undefined : 'ppm',
    },
    {
      label: 'Monotonic',
      value: scan.clock_monotonic_ns == null ? NO_VALUE : num(scan.clock_monotonic_ns / 1e9, 3),
      unit: scan.clock_monotonic_ns == null ? undefined : 's',
    },
    {
      label: 'Discipline',
      value: (
        <StateLabel
          tone={scan.clock_disciplined === true ? 'nominal' : 'warn'}
          label={scan.clock_disciplined === true ? 'NTP' : 'free-running'}
          hollow={scan.clock_disciplined == null}
        />
      ),
      mono: false,
    },
  ];

  const buildRows: KVItem[] = [
    { label: 'Firmware', value: build.firmware ?? NO_VALUE },
    { label: 'Revision', value: build.revision ?? NO_VALUE },
    { label: 'Board', value: board ?? NO_VALUE },
    {
      label: 'Bringup',
      value:
        build.commit === null ? NO_VALUE : <span title={build.commit}>{build.commit.slice(0, 7)}</span>,
    },
    ...containers.map((c) => ({
      label: c.name,
      value: c.value === null ? NO_VALUE : imageTag(c.value),
      title: c.value ?? undefined,
    })),
  ];

  return (
    <>
      <Section label="Identity" aside={site ?? undefined}>
        <KeyValue items={identity} layout="rows" />
      </Section>

      {/* Every group is a way INTO the evidence table, not a verdict printed at
          a dead end: `auterion-flight-log-review.png` runs a section index with
          per-section verdict dots and each one opens the section that failed.
          The row keeps the 20px pitch and the label/value alignment of the
          KeyValue rows above and below it — it is those rows, with a target. */}
      <Section label="Verification" aside={`${checks.filter((c) => c.ok).length}/${checks.length}`}>
        <div className={styles.checkList}>
          {checks.map((c) => (
            <button
              key={c.key}
              type="button"
              className={styles.checkRow}
              data-on={c.key === focusGroup}
              aria-controls="scan-stage-surface"
              title={`${c.hint}\n\nOpen the evidence table at the ${c.label.toLowerCase()} rows.`}
              onClick={() => onJump(c.key)}
            >
              <span className={styles.checkLabel}>
                {c.label.charAt(0) + c.label.slice(1).toLowerCase()}
              </span>
              <StateLabel tone={c.tone} label={c.value} hollow={c.hollow} />
            </button>
          ))}
        </div>
      </Section>

      <Section
        label="Ground"
        aside={
          track.length === 0 ? (
            'bbox WGS84'
          ) : (
            <span
              title={`${track.length} telemetry fixes inside the capture window, and the ground distance between them. Drawn on the plan, and cut at the lifecycle crosshair.`}
            >
              bbox · {track.length} fixes ·{' '}
              {trackMetres(track) < 1 ? '<1' : num(trackMetres(track), 0)} m
            </span>
          )
        }
      >
        {plan !== null && <SitePlan plan={plan} track={track} cursor={cursor} />}
        <KeyValue items={ground} layout="rows" />
      </Section>

      {/* Every sensor this ROBOT has ever recorded, marked for whether it was
          recording HERE. A row with a hollow dot is the finding: hardware the
          fleet knows about that this capture did not see. */}
      <Section
        label="Sensors"
        aside={`${sensors.filter((r) => r.present).length} recording`}
      >
        {sensors.length === 0 ? (
          <p className={styles.railNote}>no sensor manifest on this record</p>
        ) : (
          <>
            <KeyValue
              layout="rows"
              items={sensors.map((r) => ({
                label: r.klass,
                value: r.present ? (
                  r.id
                ) : (
                  <StateLabel tone="warn" label={r.id} hollow title="Recorded by other sessions on this robot — not by this one." />
                ),
                mono: r.present,
                /* A sensor every session of this robot carried needs no count —
                   the note under the list already says so. The count appears
                   only where it deviates, which is the only time it is news. */
                ...(r.seen === r.of ? {} : { unit: `${r.seen}/${r.of}` }),
                title: r.present
                  ? `Recording during this session · in ${r.seen} of this robot's ${r.of} sessions`
                  : `NOT recording during this session · in ${r.seen} of this robot's ${r.of} sessions`,
              }))}
            />
            <p className={styles.railNote}>{sensorNote}</p>
          </>
        )}
      </Section>

      <Section label="Build" aside="robot’s pin now">
        <KeyValue items={buildRows} layout="rows" />
        {/* The one caveat this section cannot be read without, in the voice
            the rest of the page speaks: the scan record carries `platform` and
            nothing else about the build, so everything above is the robot's
            CURRENT pin and not what ran during the capture. */}
        <p className={styles.railNote}>
          scan record stores <em>platform</em> only · not a snapshot of the capture
        </p>
      </Section>

      <Section label="Clock" aside={`±${CLOCK_OFFSET_LIMIT_MS} ms`}>
        <KeyValue items={clock} layout="rows" />
      </Section>
    </>
  );
}

/* ==========================================================================
   Re-dispatching the COPC worker — the one thing an operator can DO about a
   session that failed or was never processed.
   ========================================================================== */

function useReprocess(scanId: number | undefined, robotId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<void> => {
      const r = await apiFetch(`/api/scans/${scanId}/process`, { method: 'POST' });
      if (!r.ok) {
        const detail = (await r.json().catch(() => null)) as { detail?: unknown } | null;
        throw new Error(
          typeof detail?.detail === 'string' ? detail.detail : `status ${r.status}`,
        );
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['scan', scanId] });
      void qc.invalidateQueries({ queryKey: ['scans', robotId] });
    },
  });
}

/* ==========================================================================
   The page
   ========================================================================== */

export function ScanViewer() {
  const { scanId, navigate, robotId } = useFleetRoute();
  const id = scanId != null && scanId !== '' ? Number(scanId) : undefined;
  const valid = id != null && Number.isFinite(id);

  const scanQ = useScan(valid ? id : undefined);
  const scan = scanQ.data as FullScan | undefined;
  const scanRead = useRead(scanQ, {
    path: valid ? `/api/scans/${id}` : undefined,
    everyMs: POLL_MS,
    subject: valid ? `scan #${id}` : 'this scan',
  });

  const owner = scan?.robot_id ?? robotId;
  const robotQ = useRobot(owner);
  const robot = robotQ.data;
  const revisionsQ = useRobotRevisions(owner);
  const pinned = revisionsQ.data?.revisions.find((r) => r.id === revisionsQ.data?.current) ?? null;
  const containersQ = useContainers(robot?.morphology, pinned?.version);

  /* The robot's own series over the capture window. This is the ONLY account
     the platform keeps of what happened during the minutes the bags were
     written, and the lifecycle's three telemetry lanes are it. 600 is the
     server's own cap; a session older than the retention window gets no
     samples and the lanes say so rather than drawing one. */
  const telemetryQ = useTelemetry(owner, { limit: 600 });
  const samples = useMemo(() => samplesOf(telemetryQ.data ?? []), [telemetryQ.data]);

  /* One crosshair, two panels. Held outside React state so a pointer move on
     the ruler wakes the ground plan and nothing else — least of all the
     renderer holding a million points. */
  const cursorRef = useRef<CursorStore | null>(null);
  cursorRef.current ??= makeCursor();
  const cursor = cursorRef.current;

  /* One settlement for the two clipboard verbs this component owns — the
     header's object prefix and the artefact panel's sha256sum manifest. The
     evidence table's own copy is settled by `Stage`, which is the component
     that holds the rows. */
  const copier = useCopier();

  const siblingsQ = useRobotScans(owner);
  const siblingsRead = useRead(siblingsQ, {
    path: owner === undefined ? undefined : `/api/robots/${owner}/scans`,
    everyMs: 15_000,
    subject: owner === undefined ? 'this robot’s sessions' : `${owner}’s sessions`,
    empty: (siblingsQ.data?.length ?? 0) === 0,
  });
  /* Newest capture first, explicitly — a ledger whose order is "whatever the
     API returned" is not a ledger, and the session walk below reads its
     neighbours out of this same order. */
  const siblings = useMemo(
    () =>
      (siblingsQ.data ?? [])
        .map((s) => s as FullScan)
        .sort((a, b) => (ms(b.started_utc) ?? 0) - (ms(a.started_utc) ?? 0)),
    [siblingsQ.data],
  );

  const hasCopc = !!(scan ? scanDerivatives(scan) : []).some((d) => d.kind === 'copc' && d.storage_uri);
  const hasPreview = !!(scan ? scanDerivatives(scan) : []).some((d) => d.kind === 'copc-preview' && d.storage_uri);

  const fullQ = useDerivativeUrl(valid ? id : undefined, 'copc', hasCopc);
  const previewQ = useDerivativeUrl(valid ? id : undefined, 'copc-preview', hasPreview);

  const [chosen, setChosen] = useState<Source | null>(null);
  const source: Source = chosen ?? (hasPreview ? 'preview' : 'full');

  /* Which of the stage's two surfaces is on screen, and which verification
     group the operator arrived at it from. Null until they choose: the default
     is the surface that actually answers "did this capture work" for THIS
     session — the scene where one exists, the audit where one does not. */
  const [tabChoice, setTabChoice] = useState<StageTab | null>(null);
  const [focusGroup, setFocusGroup] = useState<string | null>(null);
  const jumpToEvidence = useCallback((key: string | null) => {
    setTabChoice('evidence');
    setFocusGroup(key);
  }, []);
  const chooseTab = useCallback((t: StageTab) => {
    setTabChoice(t);
    setFocusGroup(null);
  }, []);

  const reprocess = useReprocess(valid ? id : undefined, owner);
  /* Read once per render, not held: the token it depends on is renewed in
     place and a snapshot taken at mount would outlive it. */
  const liveCarried = realtimeConfig() !== null;

  const back = useCallback(() => {
    const target = scan?.robot_id ?? robotId;
    if (target !== undefined) navigate('detail', { robotId: target });
    else navigate('list');
  }, [scan?.robot_id, robotId, navigate]);

  const openScan = useCallback(
    (next: number) => navigate('scan', { scanId: String(next) }),
    [navigate],
  );

  /* The session walk. Triaging a night's captures is a sequence, and the
     robot's own ledger is that sequence — so `[` and `]` step it without
     going back to the robot page for every one. */
  const walkIndex = scan === undefined ? -1 : siblings.findIndex((s) => s.id === scan.id);
  const prevScan = walkIndex > 0 ? siblings[walkIndex - 1] : undefined;
  const nextScan =
    walkIndex >= 0 && walkIndex < siblings.length - 1 ? siblings[walkIndex + 1] : undefined;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      if (e.key === '[' && prevScan !== undefined) {
        e.preventDefault();
        openScan(prevScan.id);
      } else if (e.key === ']' && nextScan !== undefined) {
        e.preventDefault();
        openScan(nextScan.id);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [prevScan, nextScan, openScan]);

  if (!valid) {
    return (
      <SurfaceShell title="Scan session">
        <p className={styles.railNote}>
          No scan selected — open one from a robot’s captures. /fleet/scan/:id
        </p>
      </SurfaceShell>
    );
  }

  /* ------------------------------------------------------------------------
     THE API ANSWERED, AND THERE IS NO SUCH SESSION.

     A 404 is not an outage and not a loading state: the console is working
     perfectly and the id is wrong. So nothing on this page draws a block, a
     row or a segment that stands in for a record nobody will ever read — the
     panels keep their headers, their column headings, their lane names and
     their KPI labels, print an em dash where a single figure would go, and
     each says ONE line where its first row would be. `blankLine()` writes all
     four of those sentences, so the wording is the console's and not this
     surface's, and the way out is the surface's one primary verb, top-right,
     where every other primary on this page lives.

     `stopped` is deliberately kept for the other reason the record can be
     absent — a read that has genuinely failed or has not landed yet — because
     "we have not read this" and "there is no such thing" are different facts
     and an operator acts on them differently.
     ------------------------------------------------------------------------ */
  const absent =
    scan === undefined &&
    (scanRead.fault?.class === 'missing' || scanRead.fault?.class === 'forbidden');
  /* ONE LIVE REGION FOR ONE ANSWER.

     `Blank` marks a `missing`/`withheld` line `role="status"`, and the rail —
     the panel that carries the endpoint and the status alongside the sentence
     — is already rendering one through `ReadPanel`. Three more identical
     regions would have a screen reader read `scan #9999 is not on record` four
     times for one 404. So the other panels print the SAME house sentence from
     `blankLine()` as plain text: same words, same ink, same position, said
     once. */
  const absentLine = (
    <Blank>
      {blankLine(scanRead.fault?.class === 'forbidden' ? 'withheld' : 'missing', {
        subject: `scan #${id}`,
      })}
    </Blank>
  );
  /* One drawing per panel with no record behind it, and the only thing that
     changes between the two reasons is the sentence inside it. */
  const standing = (note: ReactNode): StandingValue =>
    absent ? { mode: 'empty', note } : { mode: 'stopped' };

  const st = scan === undefined ? null : stateOf(scan.processing_state);
  const startMs = ms(scan?.started_utc);
  const stopMs = ms(scan?.stopped_utc);
  const durMs = startMs !== null && stopMs !== null ? stopMs - startMs : null;
  const copc = (scan ? scanDerivatives(scan) : []).find((d) => d.kind === 'copc' && d.storage_uri) ?? null;
  const size = splitBytes(scan?.size_bytes);
  const rate =
    scan !== undefined && durMs !== null && durMs > 0
      ? scan.size_bytes / (durMs / 1000) / (1024 * 1024)
      : null;
  const extent = scan === undefined ? null : extentMetres(scan);
  /* ONE audit, read by everything on the page that judges this session: the
     KPI band's colours, the rail's four verification rows, the evidence table
     and the tally on its header. Recomputed on the record's own poll rather
     than on a ticker — the table mounts its own 1 Hz clock while it is the
     surface on screen, which is the only place a second matters. */
  const readAt = Date.now();
  const rows = scan === undefined ? [] : auditRows(scan, siblings, robot, readAt);
  const checks = groupsOf(rows);
  const passed = checks.filter((c) => c.ok).length;
  const pace = scan === undefined ? null : derivePace(scan, siblings, readAt);

  /* The band's verdicts. Each figure is coloured by the ONE evidence row that
     judges that figure, at the limit that row prints — so a coloured KPI is
     never a colour the operator cannot go and read the reasoning for, and the
     cell itself is the way there. A session inside every limit is a row of
     white numerals. */
  const rowOf = (id: string): AuditRow | undefined => rows.find((r) => r.id === id);
  const failed = (id: string, tone: Tone = 'warn'): Tone | undefined =>
    rowOf(id)?.verdict === 'fail' ? tone : undefined;
  /* The tooltip is the check, its reading, the limit it was judged against and
     the verdict — so the band answers "judged against what" on hover, before
     the operator has spent a click. */
  const jumpTitle = (id: string): string => {
    const r = rowOf(id);
    return r === undefined
      ? 'Open the evidence table'
      : `${r.check} · ${r.reading} · limit ${r.limit} · ${r.verdict}`;
  };
  const checksTone: Tone | undefined = checks.some((c) => c.tone === 'error')
    ? 'error'
    : checks.some((c) => !c.ok)
      ? 'warn'
      : undefined;
  const paceTone: Tone | undefined =
    scan?.processing_state === 'failed'
      ? 'error'
      : pace?.tone === 'warn' || pace?.tone === 'error'
        ? pace.tone
        : undefined;
  const meta = (robot?.meta ?? {}) as Record<string, unknown>;
  const kind = typeof meta['kind'] === 'string' ? meta['kind'] : null;
  const site = typeof meta['site'] === 'string' ? meta['site'] : null;
  const bay = typeof meta['bay'] === 'string' ? meta['bay'] : null;

  const build: Build = {
    firmware: robot?.firmware ?? null,
    revision: pinned?.version ?? null,
    commit:
      typeof (pinned as { script_sha256?: unknown } | null)?.script_sha256 === 'string'
        ? (pinned as unknown as { script_sha256: string }).script_sha256
        : null,
  };
  const board = typeof pinned?.meta['board'] === 'string' ? (pinned.meta['board'] as string) : null;
  const containers = (containersQ.data?.containers ?? []).map((c) => ({
    name: c.name,
    value: c.value,
  }));

  const plan = scan === undefined ? null : planOf(siblings.length > 0 ? siblings : [scan], scan.id);
  /* The fixes inside THIS capture's window, in the plan's own metres. */
  const track =
    scan === undefined || startMs === null || stopMs === null
      ? []
      : trackOf(
          plan,
          samples.filter((s) => s.t >= startMs && s.t <= stopMs),
        );

  /* What the sensor manifest means in context: the same set on every session
     is itself the fact an operator wants ("nothing was unplugged"), and a
     session that recorded a different set is the finding. */
  const sensorRows = scan === undefined ? [] : sensorRowsOf(scan, siblings);
  const sensorNote = (() => {
    if (scan === undefined) return '';
    const others = siblings.filter((s) => s.id !== scan.id);
    if (others.length === 0) return 'this session only · no other session to compare against';
    const missing = sensorRows.filter((r) => !r.present);
    if (missing.length > 0) {
      return `${missing.map((r) => r.id).join(' · ')} — on other sessions of this robot, not on this one`;
    }
    const key = [...scan.sensors].sort().join('|');
    const same = others.filter((s) => [...s.sensors].sort().join('|') === key).length;
    return same === others.length
      ? `same set as all ${others.length} other session${others.length === 1 ? '' : 's'} of this robot`
      : `${others.length - same} of ${others.length} other sessions recorded a different set`;
  })();

  const prefix = (() => {
    const key = objectKey((scan ? scanFiles(scan) : [])[0]?.storage_uri ?? null);
    if (key === null) return null;
    const i = key.lastIndexOf('/');
    return i < 0 ? key : key.slice(0, i);
  })();

  /* The provenance line — session · robot · platform · morphology+revision ·
     firmware · format · frame · kind · site, exactly as the robot page reads
     it, so an operator moving between the two surfaces reads the same line in
     the same order. */
  const provenance: { text: string; strong?: boolean; opt?: 1 | 2 }[] =
    scan === undefined
      ? []
      : [
          { text: scan.session_id, strong: true },
          { text: scan.robot_id },
          { text: scan.platform ?? NO_VALUE },
          { text: `${scan.morphology ?? NO_VALUE}${pinned ? ` ${pinned.version}` : ''}` },
          ...(build.firmware ? [{ text: `fw ${build.firmware}`, opt: 1 as const }] : []),
          /* The short bringup commit, inline, in the line an operator reads
             without looking for it — the third half of the build provenance
             `auterion-flights-log-table.png` puts on every row. */
          ...(build.commit
            ? [{ text: `bringup ${build.commit.slice(0, 7)}`, opt: 1 as const }]
            : []),
          { text: scan.format ?? NO_VALUE, opt: 1 as const },
          { text: `frame ${scan.pose_frame ?? NO_VALUE}`, opt: 2 as const },
          ...(kind ? [{ text: kind, opt: 2 as const }] : []),
          ...(site ? [{ text: bay ? `${site}/${bay}` : site, opt: 2 as const }] : []),
        ];

  const urlQ = source === 'preview' ? previewQ : fullQ;
  const urlError = urlQ.isError
    ? urlQ.error instanceof Error
      ? urlQ.error.message
      : 'Unknown error.'
    : null;

  /* --------------------------------------------------------------------------
     The page's one verb.

     `POST /api/scans/:id/process` re-runs the whole pipeline for one session
     (api/routers/scans.py → pipeline.lidar.copc.process_scan), from ANY state:
     the capture bags are the input and they are never touched. So a session
     without a point cloud always has a verb, including the one that is nine
     hundred times its robot's median through a run nobody is watching — which
     is exactly the session an operator opens this page to act on, and the one
     round 3 gave nothing to press.

     Emphasis follows the reading, not the state: a session inside the pace band
     gets an outlined verb, one past it gets the filled accent. That way the
     accent on this page always marks something that needs doing.
     -------------------------------------------------------------------------- */
  const dispatch: {
    label: string;
    icon: IconName;
    primary: boolean;
    title: string;
  } | null =
    scan === undefined || scan.processing_state === 'copc'
      ? null
      : scan.processing_state === 'raw'
        ? {
            label: 'Dispatch derive',
            icon: 'play',
            primary: true,
            title: `POST /api/scans/${scan.id}/process — queue this session with the COPC worker. The capture bags are the input and are not modified.`,
          }
        : scan.processing_state === 'failed'
          ? {
              label: 'Re-dispatch derive',
              icon: 'refresh',
              primary: true,
              title: `POST /api/scans/${scan.id}/process — re-queue this session with the COPC worker. The bags the failed run read are untouched, so a retry is safe.`,
            }
          : {
              label: 'Re-dispatch derive',
              icon: 'refresh',
              primary: paceTone !== undefined,
              title: `POST /api/scans/${scan.id}/process — re-queue this session with the COPC worker. It does not cancel a run already in flight; the bags are read again from object storage.`,
            };

  /* Whether a renderer will actually mount. It decides the stage's share of
     the grid, and with it whether the two ledgers get the ~440px a banner does
     not need. `Live` counts only where the transport that carries it exists —
     a stage sized for a scene that is going to render an 88px "not configured"
     banner is 470px of black pretending to be a viewport. */
  const hasScene =
    scan !== undefined &&
    (source === 'live'
      ? liveCarried
      : scan.processing_state !== 'failed' && (hasCopc || hasPreview) && urlError === null);

  /* The two shapes this page takes, and the one attribute the grid reads.

       scene     a renderer will mount and the operator is on the viewer — the
                 point cloud is the centrepiece and takes the elastic track.
       evidence  the audit is the surface on screen. Its row count grows with
                 the record — a five-bag session audits 25 rows where a two-bag
                 session audits 22 — so it takes the elastic track and the two
                 ledgers go content-height beneath it.

     Neither shape leaves a bare panel run: whichever panel can convert height
     into rows is the one handed the slack. */
  const stageTab: StageTab = hasScene ? (tabChoice ?? 'viewer') : 'evidence';
  const stageShape = stageTab === 'evidence' ? 'evidence' : 'scene';

  const artefactCount =
    scan === undefined ? undefined : scanFiles(scan).length + scanDerivatives(scan).length;

  const shaManifest =
    scan === undefined
      ? ''
      : scanFiles(scan)
          .filter((f) => f.content_sha256)
          .map((f) => `${f.content_sha256}  ${f.name}`)
          .join('\n');

  return (
    <SurfaceShell
      eyebrow={<BackLink label={robot?.name ?? scan?.robot_id ?? 'Fleet'} onClick={back} />}
      title={scan === undefined ? `Scan #${id}` : sessionName(scan.session_id)}
      subtitle={
        <>
          {st !== null && (
            <StateLabel tone={st.tone} label={st.label} loud={st.tone === 'error'} title={st.hint} />
          )}
          <span className={styles.provenance}>
            {provenance.map((p, i) => (
              <span key={p.text} className={styles.provItem} data-opt={p.opt}>
                {i > 0 && <span className={styles.provSep}>·</span>}
                <span data-strong={p.strong === true ? 'true' : undefined}>{p.text}</span>
              </span>
            ))}
          </span>
        </>
      }
      actions={
        absent ? (
          /* THE ONE SURVIVING VERB, WHERE THE PRIMARY ALWAYS IS.
             A record that is not on file has no prefix to copy and no
             derivative to dispatch, so this page's action row is the way out
             and nothing else — top-right, filled, in the same position and at
             the same weight as `Download COPC` on a session that exists. */
          <Button icon="arrow-left" variant="primary" onClick={back}>
            Back to the fleet
          </Button>
        ) : (
        <>
          {walkIndex >= 0 && siblings.length > 1 && (
            <span className={styles.walk}>
              <IconButton
                icon="chevron-left"
                label={
                  prevScan === undefined
                    ? 'No newer session on this robot'
                    : `Newer session — ${sessionName(prevScan.session_id)}  [`
                }
                disabled={prevScan === undefined}
                onClick={() => prevScan && openScan(prevScan.id)}
              />
              <span className={styles.walkAt}>
                {walkIndex + 1}/{siblings.length}
              </span>
              <IconButton
                icon="chevron-right"
                label={
                  nextScan === undefined
                    ? 'No older session on this robot'
                    : `Older session — ${sessionName(nextScan.session_id)}  ]`
                }
                disabled={nextScan === undefined}
                onClick={() => nextScan && openScan(nextScan.id)}
              />
            </span>
          )}
          <Button
            icon={copyIcon(copyStateOf(copier, 'prefix'))}
            variant="secondary"
            disabled={prefix === null}
            title={
              copyStateOf(copier, 'prefix') === 'fail'
                ? 'The browser refused clipboard access'
                : (prefix ?? 'No object key on this record')
            }
            aria-label={`Copy prefix${copyNote(copyStateOf(copier, 'prefix'))}`}
            style={copyInk(copyStateOf(copier, 'prefix'))}
            onClick={() => {
              if (prefix !== null) copier.copy(prefix, 'prefix');
            }}
          >
            Copy prefix
          </Button>
          {dispatch !== null ? (
            <Button
              icon={dispatch.icon}
              variant={dispatch.primary ? 'primary' : 'secondary'}
              disabled={reprocess.isPending}
              title={dispatch.title}
              onClick={() => reprocess.mutate()}
            >
              {reprocess.isPending ? 'Dispatching…' : dispatch.label}
            </Button>
          ) : (
            copc !== null && (
              <Button
                icon="download"
                variant="primary"
                disabled={fullQ.data === undefined}
                badge={bytes(copc.size_bytes)}
                title="Open the presigned COPC object in a new tab"
                onClick={() => {
                  const u = fullQ.data?.url;
                  if (u) window.open(u, '_blank', 'noopener,noreferrer');
                }}
              >
                Download COPC
              </Button>
            )
          )}
        </>
        )
      }
      pad="none"
    >
      <div className={styles.page} data-stage={scan === undefined ? 'evidence' : stageShape}>
        <div className={styles.stripWrap}>
          {scan === undefined ? (
            <StoppedContext.Provider value={standing(null)}>
              <SkeletonStrip cells={KPI_CELLS} what="session totals" />
            </StoppedContext.Provider>
          ) : (
            <MetricStrip>
              <KpiCell
                to="window"
                onJump={jumpToEvidence}
                title={jumpTitle('window')}
                tone={failed('window')}
              >
                <MetricCard
                  eyebrow="Duration"
                  value={durMs === null ? <Missing /> : duration(durMs)}
                  tone={failed('window')}
                  note={`${utcTime(scan.started_utc)} → ${utcTime(scan.stopped_utc)}`}
                />
              </KpiCell>
              <KpiCell
                to="reconcile"
                onJump={jumpToEvidence}
                title={jumpTitle('reconcile')}
                tone={failed('reconcile')}
              >
                <MetricCard
                  eyebrow="Captured"
                  value={size.n}
                  unit={size.u}
                  tone={failed('reconcile')}
                  note={`${scanFiles(scan).length} file${scanFiles(scan).length === 1 ? '' : 's'} · ${scan.format ?? NO_VALUE}`}
                />
              </KpiCell>
              <KpiCell
                to="rate"
                onJump={jumpToEvidence}
                title={jumpTitle('rate')}
                tone={failed('rate')}
              >
                <MetricCard
                  eyebrow="Write rate"
                  value={rate === null ? <Missing /> : num(rate, 1)}
                  unit={rate === null ? undefined : 'MiB/s'}
                  tone={failed('rate')}
                  note="mean over capture"
                />
              </KpiCell>
              <KpiCell
                to="sensors"
                onJump={jumpToEvidence}
                title={jumpTitle('sensors')}
                tone={failed('sensors')}
              >
                <MetricCard
                  eyebrow="Sensors"
                  value={scan.sensors.length}
                  tone={failed('sensors')}
                  note={scan.sensors.map(sensorClass).join(' · ') || 'none recorded'}
                />
              </KpiCell>
              <KpiCell
                to="bounds"
                onJump={jumpToEvidence}
                title={jumpTitle('bounds')}
                tone={failed('bounds')}
              >
                <MetricCard
                  eyebrow="Footprint"
                  value={extent === null ? <Missing /> : num((extent.w * extent.h) / 10_000, 1)}
                  unit={extent === null ? undefined : 'ha'}
                  tone={failed('bounds')}
                  note={extent === null ? 'no bounds' : `${num(extent.w, 0)} × ${num(extent.h, 0)} m`}
                />
              </KpiCell>
              {/* The tally, and the whole table behind it. This is the cell an
                  operator reaches for before a derivative ships downstream. */}
              <KpiCell
                to={null}
                onJump={jumpToEvidence}
                title={`${passed}/${checks.length} groups met · ${tallyOf(rows)} — open the evidence table`}
                tone={checksTone}
              >
                <MetricCard
                  eyebrow="Checks"
                  value={passed}
                  of={checks.length}
                  tone={checksTone}
                  note={
                    passed === checks.length
                      ? AUDIT_GROUPS.map((g) => g.toLowerCase()).join(' · ')
                      : checks
                          .filter((c) => !c.ok)
                          .map((c) => c.label.toLowerCase())
                          .join(' · ')
                  }
                />
              </KpiCell>
              <KpiCell to="artefact" onJump={jumpToEvidence} title={jumpTitle('artefact')}>
                <MetricCard
                  eyebrow="Derived"
                  value={copc === null ? <Missing /> : splitBytes(copc.size_bytes).n}
                  unit={copc === null ? undefined : splitBytes(copc.size_bytes).u}
                  note={
                    copc === null
                      ? (st?.label.toLowerCase() ?? NO_VALUE)
                      : `1:${Math.round(scan.size_bytes / copc.size_bytes)} of capture`
                  }
                />
              </KpiCell>
              <KpiCell
                to="elapsed"
                onJump={jumpToEvidence}
                title={jumpTitle('elapsed')}
                tone={paceTone}
              >
                <MetricCard
                  eyebrow={
                    scan.processing_state === 'copc'
                      ? 'Derive'
                      : scan.processing_state === 'failed'
                        ? 'Failed'
                        : 'In state'
                  }
                  value={
                    pace?.elapsed == null ? <Missing /> : duration(pace.elapsed)
                  }
                  tone={paceTone}
                  note={
                    scan.processing_state === 'copc'
                      ? 'stop → derivative'
                      : `since ${utcTime(scan.updated_at)}`
                  }
                />
              </KpiCell>
            </MetricStrip>
          )}
        </div>

        <PanelGrid className={styles.grid}>
          <Rail
            scan={scan}
            read={scanRead}
            robotName={robot?.name ?? null}
            build={build}
            board={board}
            containers={containers}
            kind={kind}
            site={site}
            plan={plan}
            track={track}
            cursor={cursor}
            checks={checks}
            sensors={sensorRows}
            sensorNote={sensorNote}
            focusGroup={focusGroup}
            onJump={jumpToEvidence}
          />

          <div className={styles.stage}>
            {scan === undefined ? (
              /* The instrument survives a failed read: the same tab pair, the
                 same seven column headings, the same 20px pitch, no claim. */
              <Panel
                viewport={false}
                pad="none"
                scroll={false}
                title={
                  <StageTabs
                    active="evidence"
                    onChange={chooseTab}
                    count={undefined}
                    /* "There is no such session" and "we have not read it" are
                       different facts and an operator acts on them
                       differently — the tab must not infer the first from a
                       request that failed. */
                    viewerReason={absent ? 'no session record' : 'session not read'}
                  />
                }
                meta={<span className={styles.metaWord}>{NO_VALUE}</span>}
              >
                <StoppedContext.Provider value={standing(absentLine)}>
                  <FitTable spec={AUDIT_COLUMNS} what="the evidence" flex={FLEX_AUDIT_CH} />
                </StoppedContext.Provider>
              </Panel>
            ) : (
              <Stage
                scan={scan}
                siblings={siblings}
                robot={robot}
                tab={stageTab}
                onTab={chooseTab}
                focusGroup={focusGroup}
                source={source}
                onSource={setChosen}
                previewUrl={previewQ.data?.url ?? null}
                fullUrl={fullQ.data?.url ?? null}
                urlError={urlError}
                urlLoading={previewQ.isFetching || fullQ.isFetching}
                notice={reprocess.error instanceof Error ? reprocess.error.message : null}
                liveCarried={liveCarried}
              />
            )}
          </div>

          <div className={styles.pipeline}>
            {scan === undefined ? (
              <Panel
                title={
                  <span className={styles.panelTitle}>
                    <Icon name="history" size={14} />
                    Lifecycle
                  </span>
                }
                pad="tight"
                scroll={false}
              >
                <StoppedContext.Provider value={standing(absentLine)}>
                  {/* No record, no window. The lanes keep their names and lose
                      the time grid: unlike a threshold rule, a lifecycle's
                      x-grid is not a limit the channel is judged against, it
                      is the span the console read off the record — and with no
                      record there is no span, so eleven ruled cells across two
                      lanes are segments nobody has read. `--plot-grid` is the
                      token the shared instrument draws it with, so this turns
                      it off rather than restyling the instrument. */}
                  <div className={styles.lanesOnly} data-flat="true">
                    <SkeletonPlot
                      lanes={[
                        { label: 'Capture' },
                        { label: 'Pipeline' },
                        { label: 'Mode' },
                        { label: 'Write', unit: 'MiB/s' },
                        { label: 'Battery', unit: '%' },
                      ]}
                      ticks={5}
                      what="the lifecycle"
                    />
                  </div>
                </StoppedContext.Provider>
              </Panel>
            ) : (
              <LifecyclePanel
                scan={scan}
                siblings={siblings}
                telemetry={samples}
                cursor={cursor}
              />
            )}
          </div>

          <div className={styles.ledger}>
            <Panel
              title="Artefacts"
              count={artefactCount}
              meta={
                <span className={styles.metaWord}>
                  {scan === undefined
                    ? NO_VALUE
                    : `${bucketOf(scan) ?? 'no bucket'} · ${bytes(
                        scanFiles(scan).reduce((a, f) => a + f.size_bytes, 0) +
                          scanDerivatives(scan).reduce((a, d) => a + d.size_bytes, 0),
                      )}`}
                </span>
              }
              tools={
                <IconButton
                  icon={copyIcon(copyStateOf(copier, 'manifest'))}
                  style={copyInk(copyStateOf(copier, 'manifest'))}
                  label={`Copy a sha256sum manifest for every capture file${copyNote(
                    copyStateOf(copier, 'manifest'),
                  )}`}
                  disabled={shaManifest === ''}
                  onClick={() => copier.copy(shaManifest, 'manifest')}
                />
              }
              pad="none"
            >
              {scan === undefined ? (
                <StoppedContext.Provider value={standing(<Blank noun="artefacts" />)}>
                  <FitTable spec={artefactColumns} what="artefacts" flex={FLEX_ARTEFACT_CH} />
                </StoppedContext.Provider>
              ) : (
                <ArtefactTable scan={scan} />
              )}
            </Panel>

            {scan === undefined ? (
              /* The robot is not known until the scan record lands, so this
                 read has not been ISSUED — and an instrument that says
                 "Reading" while nothing is in flight is the console claiming
                 work it is not doing. It stands down and lets the rail's own
                 fault line speak. */
              <Panel
                title="Sessions"
                meta={<span className={styles.metaWord}>{NO_VALUE} · build = pin now</span>}
                pad="none"
              >
                <StoppedContext.Provider value={standing(<Blank noun="sibling sessions" />)}>
                  <FitTable spec={SESSION_COLUMNS} what="sessions" flex={FLEX_SESSION_CH} />
                </StoppedContext.Provider>
              </Panel>
            ) : (
              <ReadPanel
                title="Sessions"
                count={siblingsRead.hasData ? siblings.length : undefined}
                read={siblingsRead}
                meta={
                  <span className={styles.metaWord}>
                    {scan.robot_id} · build = pin now
                  </span>
                }
                skeleton={<FitTable spec={SESSION_COLUMNS} what="sessions" flex={FLEX_SESSION_CH} />}
                empty={<Blank noun="sessions" where="for this robot" />}
              >
                <SessionTable
                  rows={siblings}
                  currentId={scan.id}
                  build={build}
                  robot={robot}
                  onOpen={openScan}
                />
              </ReadPanel>
            )}
          </div>
        </PanelGrid>
      </div>
    </SurfaceShell>
  );
}
